import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { 
  DocumentTextIcon, 
  ChartBarIcon, 
  AcademicCapIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline'
import AgentHeader from '../../components/AgentHeader'
import { Search } from 'lucide-react'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, LineElement, PointElement, RadialLinearScale } from 'chart.js'
import { Bar, Pie, Line, Scatter, PolarArea, Radar } from 'react-chartjs-2'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, Table, TableRow, TableCell, WidthType, ImageRun } from 'docx'
import html2canvas from 'html2canvas'
import { saveAs } from 'file-saver'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  RadialLinearScale
)

const ResearchResultPage = ({ result, onBack, language = 'id' }) => {
  const [activeSection, setActiveSection] = useState('abstract')
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)
  const [isGeneratingDOC, setIsGeneratingDOC] = useState(false)
  const [generationProgress, setGenerationProgress] = useState(0)
  const [showFullText, setShowFullText] = useState({})
  const [isCapturingCharts, setIsCapturingCharts] = useState(false)
  const chartRefs = useRef({})
  const chartInstancesRegistry = useRef(new Map()) // New registry for chart instances
  const chartRegistryBackup = useRef(new Map()) // Backup registry for subsequent downloads
  const chartObserver = useRef(null) // MutationObserver for automatic chart detection

  const { researchPaper, academicSources, relevantImages, chartImages } = result || {}

  // State untuk progress dan loading
  const [isGenerating, setIsGenerating] = useState(true)
  const [isCompleted, setIsCompleted] = useState(false)

  // Setup automatic chart detection with MutationObserver
  useEffect(() => {
    const setupChartObserver = () => {
      if (chartObserver.current) {
        chartObserver.current.disconnect()
      }
      
      chartObserver.current = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Look for canvas elements
              const canvases = node.tagName === 'CANVAS' ? [node] : node.querySelectorAll?.('canvas') || []
              
              canvases.forEach((canvas, index) => {
                if (canvas && canvas.width > 0 && canvas.height > 0) {
                  // Try to find Chart.js instance
                  const chartInstance = canvas.chart || 
                                      window.Chart?.getChart?.(canvas) ||
                                      Object.values(window.Chart?.instances || {}).find(inst => inst.canvas === canvas)
                  
                  if (chartInstance) {
                    const chartId = `auto-detected-${Date.now()}-${index}`
                    chartInstancesRegistry.current.set(chartId, {
                      instance: chartInstance,
                      canvas: canvas,
                      container: canvas.closest('[class*="chart"]') || canvas.parentElement,
                      timestamp: Date.now()
                    })
                    console.log(`🔍 Auto-detected chart instance: ${chartId}`, chartInstance)
                  } else {
                    // Store canvas even without Chart.js instance
                    const canvasId = `canvas-${Date.now()}-${index}`
                    chartInstancesRegistry.current.set(canvasId, {
                      canvas: canvas,
                      container: canvas.closest('[class*="chart"]') || canvas.parentElement,
                      timestamp: Date.now()
                    })
                    console.log(`📊 Auto-detected canvas: ${canvasId}`, canvas)
                  }
                }
              })
            }
          })
        })
      })
      
      // Start observing
      chartObserver.current.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: false
      })
      
      console.log('🔍 Chart observer initialized')
    }
    
    setupChartObserver()
    
    return () => {
      if (chartObserver.current) {
        chartObserver.current.disconnect()
      }
    }
  }, [])

  // Handle loading state based on actual data
  useEffect(() => {
    if (researchPaper && Object.keys(researchPaper).length > 0 && !isCompleted) {
      // Jika data asli sudah ada, complete the progress and set completed
      setGenerationProgress(100)
      setTimeout(() => {
        setIsGenerating(false)
        setIsCompleted(true)
      }, 500) // Small delay for smooth transition
    } else if (!researchPaper || Object.keys(researchPaper).length === 0) {
      // Jika belum ada data asli, tetap dalam state generating
      setIsGenerating(true)
      setIsCompleted(false)
      
      // Simulasi progress untuk user experience
      const progressInterval = setInterval(() => {
        setGenerationProgress(prev => {
          if (prev >= 95) {
            clearInterval(progressInterval)
            return 95 // Stop at 95% until real data arrives
          }
          return prev + Math.random() * 5
        })
      }, 1000)
      
      return () => clearInterval(progressInterval)
    }
  }, [researchPaper, isCompleted])

  // Cleanup chart instances on unmount to prevent "Canvas is already in use" errors
  useEffect(() => {
    return () => {
      console.log('🧹 Cleaning up chart instances on unmount...')
      
      // Destroy all chart instances in registry
      chartInstancesRegistry.current.forEach((chartData, key) => {
        if (chartData.instance && typeof chartData.instance.destroy === 'function') {
          try {
            console.log(`🗑️ Destroying chart instance: ${key}`)
            chartData.instance.destroy()
          } catch (error) {
            console.warn(`⚠️ Error destroying chart ${key}:`, error)
          }
        }
      })
      
      // Clear all chart references
      chartRefs.current = {}
      chartInstancesRegistry.current.clear()
      
      // Destroy any remaining Chart.js instances
      if (window.Chart && window.Chart.instances) {
        Object.values(window.Chart.instances).forEach((instance, index) => {
          try {
            console.log(`🗑️ Destroying remaining Chart.js instance: ${index}`)
            instance.destroy()
          } catch (error) {
            console.warn(`⚠️ Error destroying Chart.js instance ${index}:`, error)
          }
        })
      }
      
      console.log('✅ Chart cleanup completed')
    }
  }, [])

  // Cleanup chart instances only when component is about to re-render with different data
  const prevResearchPaper = useRef(null)
  const hasInitializedCharts = useRef(false)
  const hasCompletedFirstDownload = useRef(false)
  
  useEffect(() => {
    // CRITICAL FIX: Never clear charts after first download is completed
    // This preserves chart registry for subsequent downloads
    if (hasCompletedFirstDownload.current) {
      console.log('🔒 First download completed - chart registry is protected from clearing')
      // Update previous data reference without clearing charts
      prevResearchPaper.current = researchPaper
      return
    }
    
    // Only clear if we had previous data, charts were initialized, new data is different,
    // and we haven't completed the first download yet
    if (prevResearchPaper.current && 
        hasInitializedCharts.current && 
        researchPaper && 
        Object.keys(researchPaper).length > 0 &&
        JSON.stringify(prevResearchPaper.current) !== JSON.stringify(researchPaper)) {
      
      console.log('🔄 Research paper data changed, preparing for new charts...')
      
      // Only clear charts if data actually changed, not during capture process
      const clearCharts = () => {
         // Destroy existing chart instances
         chartInstancesRegistry.current.forEach((chartData, key) => {
           if (chartData.instance && typeof chartData.instance.destroy === 'function') {
             try {
               console.log(`🗑️ Destroying existing chart instance: ${key}`)
               chartData.instance.destroy()
             } catch (error) {
               console.warn(`⚠️ Error destroying chart ${key}:`, error)
             }
           }
         })
         
         // Clear registries
         chartRefs.current = {}
         chartInstancesRegistry.current.clear()
         hasInitializedCharts.current = false
         
         console.log('✅ Existing charts cleared for new data')
       }
       
       // Only clear if not currently capturing charts
       if (!isCapturingCharts) {
         setTimeout(clearCharts, 1000)
       }
    }
    
    // Update previous data reference
    prevResearchPaper.current = researchPaper
    
    // Mark charts as initialized when we have data and charts are rendered
    if (researchPaper && Object.keys(researchPaper).length > 0 && !hasInitializedCharts.current) {
      setTimeout(() => {
        if (chartInstancesRegistry.current.size > 0) {
          hasInitializedCharts.current = true
          console.log('✅ Charts initialized successfully')
        }
      }, 1500)
    }
  }, [researchPaper]) // Remove isCapturingCharts from dependencies

  // Generate comprehensive research content with detailed sections
  const generateDetailedContent = (basePaper = {}) => {
    // Only return actual data, no fallback template content
    if (!basePaper || Object.keys(basePaper).length === 0) {
      return null
    }
    
    const detailedPaper = {
      title: basePaper.title,
      abstract: basePaper.abstract ? {
        background: basePaper.abstract.background,
        objective: basePaper.abstract.objective,
        methods: basePaper.abstract.methods,
        results: basePaper.abstract.results,
        conclusion: basePaper.abstract.conclusion
      } : undefined,
      introduction: basePaper.introduction ? {
        background: basePaper.introduction.background,
        problemStatement: basePaper.introduction.problemStatement,
        objectives: basePaper.introduction.objectives,
        significance: basePaper.introduction.significance
      } : undefined,
      ...basePaper
    }
    return detailedPaper
  }

  // Get research paper data (only use real data, no fallback)
  const getEffectiveResearchPaper = () => {
    if (researchPaper && Object.keys(researchPaper).length > 0) {
      return researchPaper
    }
    // Return null if no real data available
    return null
  }

  // Function to get research paper with fallback for chart generation only
  const getResearchPaperForCharts = () => {
    
    // Check if we have real research paper data with valid chart data
    if (researchPaper && 
        Object.keys(researchPaper).length > 0 && 
        researchPaper.results && 
        researchPaper.results.dataVisualization && 
        Array.isArray(researchPaper.results.dataVisualization) &&
        researchPaper.results.dataVisualization.length > 0) {
      return researchPaper
    }
    
    // If we have research paper but no chart data, merge with fallback charts
    if (researchPaper && Object.keys(researchPaper).length > 0) {
      const paperWithCharts = {
        ...researchPaper,
        results: {
          ...researchPaper.results,
          dataVisualization: [
            {
              data: {
                labels: ['18-24 tahun', '25-34 tahun', '35-44 tahun', '45+ tahun'],
                values: [35, 40, 15, 10],
                chartType: 'pie'
              },
              title: 'Distribusi Demografis Responden',
              description: 'Grafik distribusi usia responden dalam penelitian'
            },
            {
              data: {
                labels: ['Baseline', 'Week 2', 'Week 4', 'Week 6', 'Week 8'],
                values: [45, 52, 61, 68, 75],
                chartType: 'line'
              },
              title: 'Tren Perkembangan Data',
              description: 'Grafik tren perkembangan data penelitian dari waktu ke waktu'
            },
            {
              data: {
                labels: ['Kelompok A', 'Kelompok B', 'Kelompok C', 'Kelompok D'],
                values: [42, 65, 58, 73],
                chartType: 'bar'
              },
              title: 'Perbandingan Antar Kelompok',
              description: 'Bar chart perbandingan hasil antar kelompok penelitian'
            }
          ]
        }
      }
      return paperWithCharts
    }
    
    // Return template data only for chart generation purposes
    const basePaper = {
      results: {
        dataVisualization: [
          {
            data: {
              labels: ['18-24 tahun', '25-34 tahun', '35-44 tahun', '45+ tahun'],
              values: [35, 40, 15, 10],
              chartType: 'pie'
            },
            title: 'Distribusi Demografis Responden',
            description: 'Grafik distribusi usia responden dalam penelitian'
          },
          {
            data: {
              labels: ['Baseline', 'Week 2', 'Week 4', 'Week 6', 'Week 8'],
              values: [45, 52, 61, 68, 75],
              chartType: 'line'
            },
            title: 'Tren Perkembangan Data',
            description: 'Grafik tren perkembangan data penelitian dari waktu ke waktu'
          },
          {
            data: {
              labels: ['Kelompok A', 'Kelompok B', 'Kelompok C', 'Kelompok D'],
              values: [42, 65, 58, 73],
              chartType: 'bar'
            },
            title: 'Perbandingan Antar Kelompok',
            description: 'Bar chart perbandingan hasil antar kelompok penelitian'
          }
        ]
      }
    }
    return basePaper
  }

  const effectiveResearchPaper = getEffectiveResearchPaper()

  // Chart color schemes - Light theme with vibrant colors optimized for white background
  const chartColors = {
    primary: ['#1e40af', '#dc2626', '#059669', '#d97706', '#7c3aed', '#0891b2', '#be185d', '#4338ca'],
    background: ['rgba(30, 64, 175, 0.8)', 'rgba(220, 38, 38, 0.8)', 'rgba(5, 150, 105, 0.8)', 'rgba(217, 119, 6, 0.8)', 'rgba(124, 58, 237, 0.8)', 'rgba(8, 145, 178, 0.8)', 'rgba(190, 24, 93, 0.8)', 'rgba(67, 56, 202, 0.8)']
  }

  // Render chart based on type with dark UI theme but optimized for white background capture
  const renderChart = (chartData, index) => {
    const { data, title, description } = chartData
    const { labels, values, chartType } = data

    const chartConfig = {
      labels: labels,
      datasets: [{
        label: title,
        data: values,
        backgroundColor: chartType === 'line' ? 'rgba(30, 64, 175, 0.1)' : chartColors.background,
        borderColor: chartColors.primary,
        borderWidth: chartType === 'pie' ? 2 : 3,
        fill: chartType === 'area',
        tension: chartType === 'line' ? 0.4 : 0,
        pointBackgroundColor: chartType === 'line' || chartType === 'scatter' ? chartColors.primary : undefined,
        pointBorderColor: chartType === 'line' || chartType === 'scatter' ? '#ffffff' : undefined,
        pointBorderWidth: chartType === 'line' || chartType === 'scatter' ? 2 : undefined,
        pointRadius: chartType === 'line' || chartType === 'scatter' ? 6 : undefined
      }]
    }

    // Light theme options optimized for capture and readability with mobile responsiveness
    const options = {
      responsive: true,
      maintainAspectRatio: false, // Allow flexible sizing for mobile
      aspectRatio: window.innerWidth < 640 ? 1.2 : 1.8, // Responsive aspect ratio
      devicePixelRatio: window.innerWidth < 640 ? 1.5 : 2, // Lower pixel ratio for mobile performance
      plugins: {
        legend: {
          position: 'top',
          labels: {
            color: '#1f2937', // Dark gray for light background
            font: {
              size: window.innerWidth < 640 ? 12 : 16, // Responsive font size
              weight: 'bold'
            },
            padding: window.innerWidth < 640 ? 10 : 20, // Responsive padding
            usePointStyle: true
          }
        },
        title: {
          display: true,
          text: title,
          color: '#111827', // Very dark for maximum contrast
          font: {
            size: window.innerWidth < 640 ? 14 : 20, // Responsive title size
            weight: 'bold'
          },
          padding: {
            top: window.innerWidth < 640 ? 8 : 15, // Responsive padding
            bottom: window.innerWidth < 640 ? 12 : 25
          }
        },
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)', // White background for tooltip
          titleColor: '#111827', // Dark title text
          bodyColor: '#374151', // Dark body text
          borderColor: '#d1d5db', // Light gray border
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: true,
          titleFont: {
            size: 14,
            weight: 'bold'
          },
          bodyFont: {
            size: 13,
            weight: '500'
          },
          padding: 12,
          caretPadding: 8,
          boxPadding: 6
        }
      },
      scales: chartType !== 'pie' && chartType !== 'radar' && chartType !== 'polarArea' ? {
        x: {
            ticks: {
              color: '#374151', // Dark gray for readability
              font: {
                size: window.innerWidth < 640 ? 10 : 14, // Responsive font size
                weight: '600'
              },
              maxRotation: window.innerWidth < 640 ? 45 : 0 // Rotate labels on mobile
            },
            grid: {
              color: '#d1d5db', // Light gray grid lines
              lineWidth: 1
            },
            title: {
              display: window.innerWidth >= 640, // Hide title on mobile to save space
              color: '#1f2937',
              font: {
                size: window.innerWidth < 640 ? 12 : 15, // Responsive font size
                weight: 'bold'
              }
            }
          },
          y: {
            ticks: {
              color: '#374151', // Dark gray for readability
              font: {
                size: window.innerWidth < 640 ? 10 : 14, // Responsive font size
                weight: '600'
              }
            },
            grid: {
              color: '#d1d5db', // Light gray grid lines
              lineWidth: 1
            },
            title: {
              display: window.innerWidth >= 640, // Hide title on mobile to save space
              color: '#1f2937',
              font: {
                size: window.innerWidth < 640 ? 12 : 15, // Responsive font size
                weight: 'bold'
              }
            }
          }
      } : {},
      elements: {
        point: {
          radius: 6,
          hoverRadius: 8,
          backgroundColor: '#ffffff',
          borderWidth: 3
        },
        line: {
          tension: 0.4,
          borderWidth: 3
        },
        bar: {
          borderWidth: 2,
          borderSkipped: false
        }
      },
      layout: {
        padding: {
          top: window.innerWidth < 640 ? 10 : 20, // Responsive padding
          right: window.innerWidth < 640 ? 10 : 25,
          bottom: window.innerWidth < 640 ? 8 : 15,
          left: window.innerWidth < 640 ? 10 : 25
        }
      },
      interaction: {
        intersect: false,
        mode: 'index'
      }
    }

    const ChartComponent = {
      bar: Bar,
      pie: Pie,
      line: Line,
      scatter: Scatter,
      polarArea: PolarArea,
      radar: Radar
    }[chartType] || Bar

    return (
      <motion.div
        key={index}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1 }}
        className="bg-bg-secondary rounded-lg p-4 sm:p-6 md:p-8 border border-border-primary mb-6 sm:mb-8 w-full" // Responsive padding and margin
        style={{ minHeight: '400px' }} // Responsive minimum height
      >
        <div 
          className="mb-4 sm:mb-6 bg-white rounded-lg p-2 sm:p-3 md:p-4 flex items-center justify-center w-full overflow-hidden" // Responsive padding and overflow control
          ref={(el) => {
            if (el) {
              chartRefs.current[`chart-${index}`] = el
            }
          }}
          style={{
            height: '280px', // Mobile-friendly height
            minHeight: '280px', // Minimum height for mobile
            maxWidth: '100%', // Ensure it doesn't exceed container
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)', // Lighter shadow for mobile
            border: '1px solid #e5e7eb' // Thinner border for mobile
          }}
        >
          <ChartComponent 
            key={`chart-${index}-${Date.now()}`}
            id={`chart-canvas-${index}-${Date.now()}`}
            data={chartConfig} 
            options={{
              ...options,
              destroyOnUnmount: true,
              animation: {
                duration: 1200, // Increased duration for smoother animation
                easing: 'easeInOutQuart',
                onComplete: function() {
                  // Register chart after animation completes with additional delay
                  const chartInstance = this
                  const canvas = chartInstance.canvas
                  
                  // Add extra delay to ensure animation is fully complete
                  setTimeout(() => {
                    if (canvas && canvas.width > 0 && canvas.height > 0) {
                      console.log(`🎯 Chart ${index} animation completed, registering...`)
                      
                      // Store references with multiple keys for reliability
                      chartRefs.current[`chart-instance-${index}`] = chartInstance
                      chartRefs.current[`chart-canvas-${index}`] = canvas
                      chartRefs.current[`chart-${index}-ready`] = {
                        instance: chartInstance,
                        canvas: canvas,
                        ready: true,
                        animationComplete: true,
                        timestamp: Date.now()
                      }
                      
                      // Register in registry with comprehensive data
                      const registryId = `chart-${index}-complete`
                      chartInstancesRegistry.current.set(registryId, {
                        instance: chartInstance,
                        canvas: canvas,
                        container: chartRefs.current[`chart-${index}`],
                        chartIndex: index,
                        source: 'animation-complete',
                        title: title || `Chart ${index + 1}`,
                        ready: true,
                        animationComplete: true,
                        timestamp: Date.now()
                      })
                      
                      // Also register with a simple key for easier access
                      chartInstancesRegistry.current.set(`chart-${index}`, {
                        instance: chartInstance,
                        canvas: canvas,
                        chartIndex: index,
                        title: title || `Chart ${index + 1}`,
                        ready: true,
                        animationComplete: true
                      })
                      
                      console.log(`✅ Chart ${index} registered successfully:`, {
                        canvasSize: `${canvas.width}x${canvas.height}`,
                        hasInstance: !!chartInstance,
                        registrySize: chartInstancesRegistry.current.size
                      })
                    }
                  }, 300) // Additional 300ms delay after animation completion
                }
              }
            }}
            ref={(chartRef) => {
              if (chartRef) {
                console.log(`🔗 Storing chart ref for index ${index}:`, chartRef)
                chartRefs.current[`chart-instance-${index}`] = chartRef
                
                // Delayed registration to allow animation to start
                setTimeout(() => {
                  let canvas = null
                  let instance = null
                  
                  if (chartRef.canvas) {
                    canvas = chartRef.canvas
                    instance = chartRef
                  } else if (chartRef.chart?.canvas) {
                    canvas = chartRef.chart.canvas
                    instance = chartRef.chart
                  }
                  
                  if (canvas && canvas.width > 0 && canvas.height > 0) {
                    chartRefs.current[`chart-canvas-${index}`] = canvas
                    
                    // Register with multiple keys for reliability
                    const registryId = `chart-${index}-ref`
                    chartInstancesRegistry.current.set(registryId, {
                      instance: instance,
                      canvas: canvas,
                      container: chartRefs.current[`chart-${index}`],
                      chartIndex: index,
                      source: 'ref-registration',
                      title: title || `Chart ${index + 1}`,
                      ready: false, // Mark as not ready during potential animation
                      animationComplete: false
                    })
                    
                    // Also store with simple index key
                    chartInstancesRegistry.current.set(`chart-ref-${index}`, {
                      instance: instance,
                      canvas: canvas,
                      chartIndex: index,
                      title: title || `Chart ${index + 1}`,
                      ready: false, // Mark as not ready during potential animation
                      animationComplete: false
                    })
                    
                    console.log(`🔄 Ref registration for chart ${index} completed:`, {
                      canvasSize: `${canvas.width}x${canvas.height}`,
                      registrySize: chartInstancesRegistry.current.size
                    })
                  }
                }, 800) // Increased delay to allow animation to start
                
                // Final registration after animation should be complete
                setTimeout(() => {
                  let canvas = null
                  let instance = null
                  
                  if (chartRef.canvas) {
                    canvas = chartRef.canvas
                    instance = chartRef
                  } else if (chartRef.chart?.canvas) {
                    canvas = chartRef.chart.canvas
                    instance = chartRef.chart
                  }
                  
                  if (canvas && canvas.width > 0 && canvas.height > 0) {
                    const backupId = `chart-${index}-backup`
                    chartInstancesRegistry.current.set(backupId, {
                      instance: instance,
                      canvas: canvas,
                      container: chartRefs.current[`chart-${index}`],
                      chartIndex: index,
                      source: 'backup-registration',
                      title: title || `Chart ${index + 1}`,
                      ready: true,
                      animationComplete: true, // Should be complete by now
                      timestamp: Date.now()
                    })
                    
                    console.log(`🔄 Backup registration for chart ${index} completed`)
                  }
                }, 2000) // Increased to 2000ms to ensure animation completion
              }
            }}
          />
        </div>
        <div className="text-center px-2">
          <h3 className="text-text-primary text-base sm:text-lg font-semibold mb-2 break-words">{title}</h3>
          <p className="text-text-secondary text-xs sm:text-sm leading-relaxed break-words">{description}</p>
        </div>
      </motion.div>
    )
  }

  // Improved function to capture chart as image with better reliability
  const captureChartAsImage = async (chartInstance, chartTitle, retryCount = 0) => {
    try {
      if (!chartInstance) {
        console.warn(`❌ No chart instance provided for ${chartTitle}`)
        return null
      }
      
      console.log(`📸 Capturing chart: ${chartTitle} (attempt ${retryCount + 1})`)
      
      // CRITICAL FIX: Force stop any ongoing animations before capture
      let actualChartInstance = null
      if (chartInstance.canvas) {
        actualChartInstance = chartInstance
      } else if (chartInstance.chart?.canvas) {
        actualChartInstance = chartInstance.chart
      } else if (chartInstance.tagName === 'CANVAS') {
        actualChartInstance = chartInstance.chart || window.Chart?.getChart?.(chartInstance)
      }
      
      // Force stop animations to ensure static capture
      if (actualChartInstance && typeof actualChartInstance.stop === 'function') {
        actualChartInstance.stop()
        console.log(`🛑 Forced animation stop for ${chartTitle}`)
      }
      
      // Wait for chart to be fully rendered with enhanced progressive delay
      const baseDelay = 2000 // Increased to 2000ms for better stability
      const retryDelay = retryCount * 800 // Increased retry delay
      const totalDelay = baseDelay + retryDelay
      
      console.log(`⏳ Waiting ${totalDelay}ms for chart to stabilize...`)
      await new Promise(resolve => setTimeout(resolve, totalDelay))
      
      // Force render update if possible
      if (actualChartInstance && typeof actualChartInstance.update === 'function') {
        actualChartInstance.update('none') // Update without animation
        console.log(`🔄 Forced chart update for ${chartTitle}`)
        await new Promise(resolve => setTimeout(resolve, 500)) // Additional wait after update
      }
      
      let canvas = null
      
      // Determine canvas and chart instance with improved detection
      if (chartInstance.canvas) {
        // Direct Chart.js instance
        console.log(`🎨 Using Chart.js instance canvas for ${chartTitle}`)
        canvas = chartInstance.canvas
        actualChartInstance = chartInstance
      } else if (chartInstance.chart?.canvas) {
        // Nested chart structure (react-chartjs-2 v3)
        console.log(`🎨 Using nested chart.canvas for ${chartTitle}`)
        canvas = chartInstance.chart.canvas
        actualChartInstance = chartInstance.chart
      } else if (chartInstance.tagName === 'CANVAS') {
        // Direct canvas element
        console.log(`🎨 Using direct canvas element for ${chartTitle}`)
        canvas = chartInstance
        actualChartInstance = chartInstance.chart || window.Chart?.getChart?.(chartInstance)
      } else {
        console.warn(`❌ Unable to determine canvas from chart instance for ${chartTitle}`)
        return null
      }
      
      // Validate canvas dimensions
      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        console.warn(`❌ Invalid canvas dimensions for ${chartTitle}: ${canvas?.width || 0}x${canvas?.height || 0}`)
        if (retryCount < 2) {
          console.log(`🔄 Retrying capture for ${chartTitle}...`)
          return await captureChartAsImage(chartInstance, chartTitle, retryCount + 1)
        }
        return null
      }
      
      console.log(`✅ Valid canvas found for ${chartTitle}: ${canvas.width}x${canvas.height}`)
      
      // Create high-quality image capture with improved methods
      let dataUrl = null
      let blob = null
      let arrayBuffer = null
      let captureCanvas = null
      
      try {
        // CRITICAL FIX: Always use direct canvas capture for reliability
        console.log(`📊 Using direct canvas capture for ${chartTitle}`)
        
        // Create enhanced canvas with white background and proper aspect ratio
        const enhancedCanvas = document.createElement('canvas')
        const ctx = enhancedCanvas.getContext('2d')
        const scale = 2 // High resolution for better quality
        
        // Preserve original aspect ratio
        const originalWidth = canvas.width
        const originalHeight = canvas.height
        
        // Set canvas dimensions maintaining aspect ratio
        enhancedCanvas.width = originalWidth * scale
        enhancedCanvas.height = originalHeight * scale
        
        // CRITICAL: Set white background for document compatibility
        ctx.fillStyle = '#FFFFFF'
        ctx.fillRect(0, 0, enhancedCanvas.width, enhancedCanvas.height)
        
        // Scale and draw the original canvas with proper settings
        ctx.scale(scale, scale)
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        
        // CRITICAL FIX: Ensure canvas is visible and has content before drawing
        if (canvas.width > 0 && canvas.height > 0) {
          ctx.drawImage(canvas, 0, 0, originalWidth, originalHeight)
          console.log(`✅ Canvas drawn successfully: ${originalWidth}x${originalHeight}`)
        } else {
          throw new Error(`Invalid canvas dimensions: ${canvas.width}x${canvas.height}`)
        }
        
        captureCanvas = enhancedCanvas
        dataUrl = enhancedCanvas.toDataURL('image/png', 1.0)
        
        // Create blob from enhanced canvas
        blob = await new Promise((resolve, reject) => {
          enhancedCanvas.toBlob((result) => {
            if (result && result.size > 1000) { // Ensure blob has content
              resolve(result)
            } else {
              reject(new Error('Generated blob is empty or too small'))
            }
          }, 'image/png', 1.0)
        })
        
        // CRITICAL: Validate dataUrl with stricter checks
        if (!dataUrl || dataUrl === 'data:,' || dataUrl.length < 5000) { // Increased minimum size
          throw new Error(`Generated dataUrl is invalid or too small: ${dataUrl?.length || 0} bytes`)
        }
        
        // Validate that dataUrl contains actual image data (not just header)
        if (!dataUrl.includes('iVBORw0KGgo')) { // PNG signature in base64
          throw new Error('DataUrl does not contain valid PNG data')
        }
        
        // Convert blob to arrayBuffer for Word document
        arrayBuffer = await blob.arrayBuffer()
        
        // CRITICAL: Final validation with detailed checks
        if (!arrayBuffer || arrayBuffer.byteLength < 1000) {
          throw new Error(`Failed to create valid arrayBuffer: ${arrayBuffer?.byteLength || 0} bytes`)
        }
        
        console.log(`✅ Successfully captured ${chartTitle}:`, {
          originalCanvasSize: `${canvas.width}x${canvas.height}`,
          captureCanvasSize: `${captureCanvas.width}x${captureCanvas.height}`,
          dataUrlLength: dataUrl.length,
          blobSize: blob.size,
          arrayBufferSize: arrayBuffer.byteLength,
          hasValidPNGData: dataUrl.includes('iVBORw0KGgo')
        })
        
        return {
          canvas: captureCanvas, // Return the enhanced canvas
          dataUrl,
          blob,
          arrayBuffer,
          title: chartTitle,
          isHighResolution: true,
          originalCanvas: canvas // Keep reference to original
        }
        
      } catch (captureError) {
        console.warn(`⚠️ Primary capture failed for ${chartTitle}, trying html2canvas:`, captureError)
        
        // Fallback: Use html2canvas for difficult cases
        try {
          const html2canvas = (await import('html2canvas')).default
          const container = canvas.parentElement || document.body
          
          const fallbackCanvas = await html2canvas(container, {
            backgroundColor: '#FFFFFF',
            scale: 1.5,
            logging: false,
            useCORS: true,
            allowTaint: true,
            width: canvas.width,
            height: canvas.height,
            canvas: canvas
          })
          
          canvas = fallbackCanvas
          dataUrl = canvas.toDataURL('image/png', 1.0)
          
          // Convert to blob and arrayBuffer for DOCX usage
          blob = await new Promise((resolve, reject) => {
            canvas.toBlob((result) => {
              if (result) {
                resolve(result)
              } else {
                reject(new Error('Failed to create blob from canvas'))
              }
            }, 'image/png', 1.0)
          })
          
          arrayBuffer = await blob.arrayBuffer()
          
          if (arrayBuffer.byteLength === 0) {
            throw new Error('Empty arrayBuffer generated')
          }
          
          console.log(`✅ html2canvas fallback successful for ${chartTitle}`)
          
        } catch (fallbackError) {
          console.error(`❌ html2canvas fallback also failed for ${chartTitle}:`, fallbackError)
          throw captureError
        }
      }
      
      // Final validation of captured image
      if (!dataUrl || dataUrl === 'data:,' || dataUrl.length < 1000) {
        console.warn(`❌ Invalid or too small image data for ${chartTitle}, retrying...`)
        if (retryCount < 2) {
          return await captureChartAsImage(chartInstance, chartTitle, retryCount + 1)
        }
        return null
      }
      
      // Final fallback for blob/arrayBuffer if not created yet
      if (!blob || !arrayBuffer) {
        try {
          if (!blob) {
            const response = await fetch(dataUrl)
            blob = await response.blob()
          }
          
          if (!arrayBuffer) {
            arrayBuffer = await blob.arrayBuffer()
          }
          
          if (arrayBuffer.byteLength === 0) {
            throw new Error('Empty arrayBuffer generated')
          }
          
        } catch (finalError) {
          console.error(`❌ Final blob/arrayBuffer conversion failed for ${chartTitle}:`, finalError)
          if (retryCount < 2) {
            return await captureChartAsImage(chartInstance, chartTitle, retryCount + 1)
          }
          return null
        }
      }
      
      console.log(`✅ Successfully captured chart: ${chartTitle}`, {
        canvasSize: `${canvas.width}x${canvas.height}`,
        dataUrlLength: dataUrl.length,
        blobSize: blob.size,
        arrayBufferSize: arrayBuffer.byteLength,
        retryCount
      })
      
      return {
        canvas,
        dataUrl,
        blob,
        arrayBuffer,
        title: chartTitle,
        isHighResolution: true
      }
    } catch (error) {
      console.error(`Error capturing chart ${chartTitle} (attempt ${retryCount + 1}):`, error)
      if (retryCount < 3) {
        console.log(`Retrying chart capture for ${chartTitle}...`)
        return await captureChartAsImage(chartInstance, chartTitle, retryCount + 1)
      }
      return null
    }
  }

  // Function to wait for all chart animations to complete
  const waitForAllAnimationsComplete = async (maxWaitTime = 8000) => {
    console.log('⏳ Waiting for all chart animations to complete...')
    const startTime = Date.now()
    
    while (Date.now() - startTime < maxWaitTime) {
      const incompleteCharts = Array.from(chartInstancesRegistry.current.entries())
        .filter(([id, entry]) => !entry.animationComplete && entry.instance)
      
      if (incompleteCharts.length === 0) {
        console.log('✅ All chart animations completed')
        return true
      }
      
      console.log(`🔄 Waiting for ${incompleteCharts.length} charts to complete animation...`)
      
      // Check if any Chart.js instances are still animating
      let stillAnimating = false
      for (const [id, entry] of incompleteCharts) {
        if (entry.instance && entry.instance.isAnimating && typeof entry.instance.isAnimating === 'function') {
          if (entry.instance.isAnimating()) {
            stillAnimating = true
            console.log(`🎬 Chart ${id} still animating...`)
          } else {
            // Mark as animation complete if Chart.js reports it's done
            entry.animationComplete = true
            console.log(`✅ Chart ${id} animation completed`)
          }
        }
      }
      
      if (!stillAnimating) {
        // Mark all remaining charts as animation complete
        incompleteCharts.forEach(([id, entry]) => {
          entry.animationComplete = true
        })
        console.log('✅ All remaining charts marked as animation complete')
        return true
      }
      
      await new Promise(resolve => setTimeout(resolve, 200))
    }
    
    console.log('⚠️ Timeout waiting for animations, proceeding with capture...')
    return false
  }

  // Force chart rescan function
  const forceChartRescan = async () => {
    console.log('🔍 Force chart rescan initiated...')
    const allCanvases = document.querySelectorAll('canvas')
    console.log(`📊 Found ${allCanvases.length} canvas elements in DOM`)
    
    // Register all valid charts found in DOM
    allCanvases.forEach((canvas, index) => {
      if (canvas && canvas.width > 0 && canvas.height > 0 && canvas.offsetParent !== null) {
        const chartInstance = canvas.chart || window.Chart?.getChart?.(canvas)
        
        // Register both with and without Chart.js instance
        const registryId = `rescan-chart-${index}`
        chartInstancesRegistry.current.set(registryId, {
          instance: chartInstance,
          canvas: canvas,
          container: canvas.closest('[class*="chart"]') || canvas.parentElement,
          chartIndex: index,
          source: 'force-rescan',
          title: `Chart ${index + 1}`,
          ready: true,
          animationComplete: true,
          timestamp: Date.now()
        })
        
        console.log(`🔄 Force rescan registration for chart ${index}:`, {
          hasInstance: !!chartInstance,
          canvasSize: `${canvas.width}x${canvas.height}`,
          visible: canvas.offsetParent !== null
        })
      }
    })
    
    console.log(`✅ Force rescan completed. Registry now has ${chartInstancesRegistry.current.size} entries`)
  }

  // Function to capture all charts with enhanced reliability
  const captureAllCharts = async () => {
    // Set capturing flag to prevent chart cleanup during capture
    setIsCapturingCharts(true)
    
    try {
      let capturedChartImages = []
      const researchPaperForCharts = getResearchPaperForCharts()
      const expectedChartCount = researchPaperForCharts?.results?.dataVisualization?.length || 0
      
      console.log('🎯 Starting enhanced chart capture process...')
      
      // CRITICAL: Wait for all animations to complete before capturing
      await waitForAllAnimationsComplete()
      
      // Additional validation: ensure all charts are truly ready
      console.log('🔍 Validating chart readiness after animation wait...')
      let readyCount = 0
      for (const [id, entry] of chartInstancesRegistry.current.entries()) {
        if (entry.instance && entry.animationComplete && entry.ready) {
          // Double-check Chart.js animation status
          if (entry.instance.isAnimating && typeof entry.instance.isAnimating === 'function') {
            if (!entry.instance.isAnimating()) {
              readyCount++
              console.log(`✅ Chart ${id} confirmed ready`)
            } else {
              console.log(`⚠️ Chart ${id} still animating despite being marked complete`)
              // Force mark as complete if we've waited long enough
              entry.animationComplete = true
              readyCount++
            }
          } else {
            readyCount++
            console.log(`✅ Chart ${id} ready (no animation check available)`)
          }
        } else if (entry.canvas && entry.canvas.width > 0) {
          // Mark charts with valid canvas as ready even without instance
          entry.ready = true
          entry.animationComplete = true
          readyCount++
          console.log(`✅ Chart ${id} marked ready (canvas-based)`)
        }
      }
      
      console.log(`📊 Chart readiness validation: ${readyCount}/${chartInstancesRegistry.current.size} charts ready`)
      console.log('📊 Available chart refs:', Object.keys(chartRefs.current))
      console.log('📋 Registry entries:', Array.from(chartInstancesRegistry.current.keys()))
      console.log('📈 Data visualization count:', expectedChartCount)
      console.log('🔍 Has completed first download:', hasCompletedFirstDownload.current)
      console.log('💾 Backup registry size:', chartRegistryBackup.current.size)
      
      // Check if we have saved chartImages from database first
      const savedChartImages = chartImages || []
      if (savedChartImages && savedChartImages.length > 0) {
        console.log('💾 Found saved chart images from database:', savedChartImages.length)
        // Use saved chart images if available
        const validChartImages = savedChartImages.filter(img => img && img.dataUrl && img.dataUrl.length > 1000)
        if (validChartImages.length > 0) {
          console.log('✅ Using saved chart images from database:', validChartImages.length)
          return validChartImages
        }
      }
      
      // CRITICAL: Always restore from backup if registry is empty and we have backup
      if (chartInstancesRegistry.current.size === 0 && chartRegistryBackup.current.size > 0) {
        console.log('🔄 Registry empty, restoring from persistent backup...')
        chartInstancesRegistry.current = new Map(chartRegistryBackup.current)
        console.log('✅ Registry restored from backup:', chartInstancesRegistry.current.size)
        
        // Also restore chart refs from backup if available
        chartRegistryBackup.current.forEach((entry, key) => {
          if (entry.canvas && entry.instance) {
            chartRefs.current[`chart-instance-${entry.chartIndex || key}`] = entry.instance
            chartRefs.current[`chart-canvas-${entry.chartIndex || key}`] = entry.canvas
            chartRefs.current[`chart-${entry.chartIndex || key}-ready`] = entry
          }
        })
        console.log('✅ Chart refs also restored from backup')
        
        // Force a chart rescan if registry is still empty after restoration
        if (chartInstancesRegistry.current.size === 0) {
          console.log('⚠️ Registry still empty after backup restoration, forcing chart rescan...')
          await forceChartRescan()
        }
      }
      
      // CRITICAL FIX: Enhanced chart stabilization and animation control
      console.log('🔧 Starting enhanced chart stabilization process...')
      
      // Force stop all animations immediately for reliable capture
      const forceStopAllAnimations = () => {
        console.log('🛑 Force stopping all chart animations for capture...')
        let stoppedCount = 0
        for (const [id, entry] of chartInstancesRegistry.current.entries()) {
          if (entry.instance) {
            if (typeof entry.instance.stop === 'function') {
              entry.instance.stop()
              stoppedCount++
              console.log(`🛑 Stopped animation for chart ${id}`)
            }
            if (typeof entry.instance.update === 'function') {
              entry.instance.update('none') // Force update without animation
              console.log(`🔄 Updated chart ${id} without animation`)
            }
            // Mark as animation complete
            entry.animationComplete = true
            entry.ready = true
          }
        }
        console.log(`✅ Stopped animations for ${stoppedCount} charts`)
        return stoppedCount
      }
      
      forceStopAllAnimations()
      
      // Extended stabilization wait after forced animation stop
      console.log('⏳ Extended stabilization wait (3500ms) after animation control...')
      await new Promise(resolve => setTimeout(resolve, 3500))
    
    // CRITICAL: Enhanced chart detection and comprehensive validation
    console.log('🔍 Performing comprehensive chart detection and validation...')
    
    // Always perform a rescan to ensure we have the most current chart state
    console.log('📊 Performing comprehensive chart rescan for reliability...')
    await forceChartRescan()
    
    // Validate and clean the registry
    const validateAndCleanRegistry = () => {
      const validEntries = new Map()
      let removedCount = 0
      
      for (const [id, entry] of chartInstancesRegistry.current.entries()) {
        // Validate canvas exists and has proper dimensions
        if (entry.canvas && 
            entry.canvas.width > 0 && 
            entry.canvas.height > 0 && 
            entry.canvas.offsetParent !== null) {
          
          // Ensure entry has all required properties
          const validatedEntry = {
            ...entry,
            ready: true,
            animationComplete: true,
            validated: true,
            validationTimestamp: Date.now()
          }
          
          validEntries.set(id, validatedEntry)
          console.log(`✅ Validated chart entry: ${id} (${entry.canvas.width}x${entry.canvas.height})`)
        } else {
          console.log(`🗑️ Removing invalid chart entry: ${id}`, {
            hasCanvas: !!entry.canvas,
            canvasSize: entry.canvas ? `${entry.canvas.width}x${entry.canvas.height}` : 'none',
            hasOffsetParent: entry.canvas ? !!entry.canvas.offsetParent : false
          })
          removedCount++
        }
      }
      
      chartInstancesRegistry.current = validEntries
      console.log(`🧹 Registry validation completed: ${validEntries.size} valid, ${removedCount} removed`)
      return validEntries.size
    }
    
    const validChartCount = validateAndCleanRegistry()
    
    // Additional wait after validation and cleanup
    console.log('⏳ Additional stabilization wait after validation...')
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // Debug: Log current state
    console.log('🔍 Debug - Current state after wait:')
    console.log('  Chart refs:', Object.keys(chartRefs.current))
    console.log('  Registry size:', chartInstancesRegistry.current.size)
    console.log('  Registry entries:', Array.from(chartInstancesRegistry.current.entries()).map(([key, value]) => ({
      key,
      hasInstance: !!value.instance,
      hasCanvas: !!value.canvas,
      canvasSize: value.canvas ? `${value.canvas.width}x${value.canvas.height}` : 'none',
      ready: value.ready
    })))
    
    // Method 1: Priority capture from registry (most reliable)
    console.log('🔍 Method 1: Priority registry capture...')
    console.log(`📋 Registry has ${chartInstancesRegistry.current.size} entries`)
    
    // Sort registry entries by animation completion status and readiness
    const sortedEntries = Array.from(chartInstancesRegistry.current.entries()).sort(([idA, entryA], [idB, entryB]) => {
      // Prioritize entries with completed animations
      const scoreA = (entryA.animationComplete ? 100 : 0) + (entryA.ready ? 50 : 0) + (entryA.timestamp ? 10 : 0)
      const scoreB = (entryB.animationComplete ? 100 : 0) + (entryB.ready ? 50 : 0) + (entryB.timestamp ? 10 : 0)
      return scoreB - scoreA // Higher score first
    })
    
    console.log('📊 Registry entries sorted by readiness:', sortedEntries.map(([id, entry]) => ({
      id,
      ready: entry.ready,
      animationComplete: entry.animationComplete,
      hasTimestamp: !!entry.timestamp
    })))
    
    for (const [id, entry] of sortedEntries) {
      if (entry.canvas && entry.canvas.width > 0 && entry.canvas.height > 0) {
        try {
          console.log(`📸 Attempting priority capture from registry: ${id}`, {
            hasInstance: !!entry.instance,
            ready: entry.ready,
            animationComplete: entry.animationComplete,
            canvasSize: `${entry.canvas.width}x${entry.canvas.height}`,
            source: entry.source
          })
          
          const target = entry.instance || entry.canvas
          const imageData = await captureChartAsImage(target, entry.title || id)
          
          if (imageData && imageData.dataUrl && imageData.dataUrl.length > 1000) {
            capturedChartImages.push({
              ...imageData,
              canvasElement: entry.canvas,
              chartIndex: entry.chartIndex || capturedChartImages.length,
              source: 'priority-registry'
            })
            console.log(`✅ Successfully captured from priority registry: ${id}`)
          }
        } catch (error) {
          console.error(`❌ Failed priority capture from registry ${id}:`, error)
        }
      }
    }
    
    // Method 2: Direct canvas search as backup
    if (capturedChartImages.length < expectedChartCount) {
      console.log('🔍 Method 2: Direct canvas backup search...')
      const directCanvases = document.querySelectorAll('canvas')
      console.log(`📊 Found ${directCanvases.length} canvas elements for backup search`)
      
      for (let i = 0; i < directCanvases.length && capturedChartImages.length < expectedChartCount; i++) {
        const canvas = directCanvases[i]
        
        // Skip if already captured
        const alreadyCaptured = capturedChartImages.some(img => img.canvasElement === canvas)
        if (alreadyCaptured) continue
        
        if (canvas && canvas.width > 0 && canvas.height > 0 && canvas.offsetParent !== null) {
          try {
            console.log(`📸 Backup capture attempt on canvas ${i}`)
            
            const chartInstance = canvas.chart || window.Chart?.getChart?.(canvas)
            const chartTitle = `Chart ${capturedChartImages.length + 1}`
            const imageData = await captureChartAsImage(chartInstance || canvas, chartTitle)
            
            if (imageData && imageData.dataUrl && imageData.dataUrl.length > 1000) {
              capturedChartImages.push({
                ...imageData,
                canvasElement: canvas,
                chartIndex: capturedChartImages.length,
                source: 'direct-canvas-backup'
              })
              console.log(`✅ Backup canvas capture successful: ${i}`)
            }
          } catch (error) {
            console.error(`❌ Backup canvas capture failed ${i}:`, error)
          }
        }
      }
    }
    
    // Method 3: Chart refs as additional backup
    if (capturedChartImages.length < expectedChartCount) {
      console.log('🔍 Method 3: Chart refs backup...')
      const chartRefKeys = Object.keys(chartRefs.current).filter(key => 
        key.includes('chart-instance-') || key.includes('chart-canvas-') || key.includes('chart-') && key.includes('-ready')
      )
      console.log('📋 Chart ref keys found:', chartRefKeys)
      
      for (const refKey of chartRefKeys) {
        if (capturedChartImages.length >= expectedChartCount) break
        
        const chartRef = chartRefs.current[refKey]
        if (chartRef) {
          let canvas = null
          let chartInstance = null
          
          // Determine canvas and instance
          if (chartRef.canvas) {
            canvas = chartRef.canvas
            chartInstance = chartRef
          } else if (chartRef.chart?.canvas) {
            canvas = chartRef.chart.canvas
            chartInstance = chartRef.chart
          } else if (chartRef.tagName === 'CANVAS') {
            canvas = chartRef
            chartInstance = canvas.chart || window.Chart?.getChart?.(canvas)
          } else if (chartRef.instance && chartRef.canvas) {
            canvas = chartRef.canvas
            chartInstance = chartRef.instance
          }
          
          // Skip if already captured
          const alreadyCaptured = capturedChartImages.some(img => img.canvasElement === canvas)
          if (alreadyCaptured) continue
          
          if (canvas && canvas.width > 0 && canvas.height > 0) {
            try {
              console.log(`📸 Chart refs backup capture: ${refKey}`)
              const imageData = await captureChartAsImage(chartInstance || canvas, refKey)
              
              if (imageData && imageData.dataUrl && imageData.dataUrl.length > 1000) {
                capturedChartImages.push({
                  ...imageData,
                  canvasElement: canvas,
                  chartIndex: capturedChartImages.length,
                  source: 'chartRefs-backup'
                })
                console.log(`✅ Chart refs backup successful: ${refKey}`)
              }
            } catch (error) {
              console.error(`❌ Chart refs backup failed ${refKey}:`, error)
            }
          }
        }
      }
    }
    

    
    // Log final results with detailed analysis
    console.log(`📊 Enhanced chart capture completed. Total images: ${capturedChartImages.length}`)
    console.log(`📈 Expected: ${expectedChartCount}, Captured: ${capturedChartImages.length}, Success rate: ${((capturedChartImages.length / Math.max(expectedChartCount, 1)) * 100).toFixed(1)}%`)
    
    capturedChartImages.forEach((img, index) => {
      console.log(`  Image ${index + 1}:`, {
        hasData: !!img.dataUrl,
        dataUrlLength: img.dataUrl?.length || 0,
        hasBlob: !!img.blob,
        blobSize: img.blob?.size || 0,
        hasArrayBuffer: !!img.arrayBuffer,
        arrayBufferSize: img.arrayBuffer?.byteLength || 0,
        source: img.source,
        chartIndex: img.chartIndex,
        title: img.title || (language === 'id' ? 'Tanpa Judul' : 'Untitled'),
        canvasSize: img.canvasElement ? `${img.canvasElement.width}x${img.canvasElement.height}` : 'unknown',
        isTextFallback: !!img.isTextFallback
      })
    })
    
    // Additional debugging info
    console.log('🔍 Final debugging summary:')
    console.log('  Total canvas elements found:', document.querySelectorAll('canvas').length)
    console.log('  Chart refs available:', Object.keys(chartRefs.current).length)
    console.log('  Registry entries:', chartInstancesRegistry.current.size)
    console.log('  Successful captures by source:', {
      'direct-canvas': capturedChartImages.filter(img => img.source === 'direct-canvas').length,
      'registry': capturedChartImages.filter(img => img.source === 'registry').length,
      'chartRefs': capturedChartImages.filter(img => img.source === 'chartRefs').length,
      'aggressive-search': capturedChartImages.filter(img => img.source === 'aggressive-search').length,
      'html2canvas-container': capturedChartImages.filter(img => img.source === 'html2canvas-container').length,
      'text-fallback': capturedChartImages.filter(img => img.source === 'text-fallback').length
    })
    
    console.log(`📈 Expected ${expectedChartCount} charts, captured ${capturedChartImages.length} images`)
    
    // Method 4: Aggressive search for any chart-related elements
    if (capturedChartImages.length < expectedChartCount) {
      console.log('🔍 Method 4: Aggressive chart element search...')
      const potentialChartElements = document.querySelectorAll(
        'canvas, [class*="chart"], .chart-container, [data-chart], [id*="chart"], .recharts-wrapper, .chartjs-render-monitor'
      )
      console.log(`📊 Found ${potentialChartElements.length} potential chart elements`)
      
      for (let i = 0; i < potentialChartElements.length && capturedChartImages.length < expectedChartCount; i++) {
        const element = potentialChartElements[i]
        let canvas = null
        
        // Find canvas within or as the element
        if (element.tagName === 'CANVAS') {
          canvas = element
        } else {
          canvas = element.querySelector('canvas')
        }
        
        if (canvas && canvas.width > 0 && canvas.height > 0) {
          // Skip if already captured
          const alreadyCaptured = capturedChartImages.some(img => img.canvasElement === canvas)
          if (alreadyCaptured) continue
          
          try {
            console.log(`📸 Attempting aggressive capture on element ${i}:`, {
              tagName: element.tagName,
              className: element.className,
              canvasSize: `${canvas.width}x${canvas.height}`
            })
            
            // Use the improved captureChartAsImage function
            const chartInstance = canvas.chart || window.Chart?.getChart?.(canvas)
            const chartTitle = `Chart ${capturedChartImages.length + 1}`
            const imageData = await captureChartAsImage(chartInstance || canvas, chartTitle)
            
            if (imageData && imageData.dataUrl && imageData.dataUrl.length > 1000) {
              capturedChartImages.push({
                ...imageData,
                canvasElement: canvas,
                chartIndex: capturedChartImages.length,
                source: 'aggressive-search'
              })
              console.log(`✅ Successfully captured element ${i} aggressively`)
            }
          } catch (error) {
            console.error(`❌ Aggressive capture failed for element ${i}:`, error)
          }
        }
      }
    }
     
    // Method 5: Final fallback using html2canvas on chart containers
    if (capturedChartImages.length < expectedChartCount) {
      console.log('🔍 Method 5: html2canvas fallback on chart containers...')
      const chartContainers = document.querySelectorAll('[class*="chart"], .chart-container, [data-chart]')
      console.log(`📊 Found ${chartContainers.length} potential chart containers`)
     
     for (let i = 0; i < chartContainers.length && capturedChartImages.length < expectedChartCount; i++) {
       const container = chartContainers[i]
       const canvas = container.querySelector('canvas')
       
       if (canvas && canvas.width > 0 && canvas.height > 0) {
         // Skip if already captured
         const alreadyCaptured = capturedChartImages.some(img => img.canvasElement === canvas)
         if (alreadyCaptured) continue
         
         try {
           console.log(`📸 Attempting html2canvas on container ${i}`)
           const html2canvas = (await import('html2canvas')).default
           
           const capturedCanvas = await html2canvas(container, {
             backgroundColor: '#FFFFFF',
             scale: 1.5,
             logging: false,
             useCORS: true,
             allowTaint: true,
             width: canvas.width,
             height: canvas.height
           })
           
           const dataUrl = capturedCanvas.toDataURL('image/png', 1.0)
           
           if (dataUrl && dataUrl.length > 1000) {
             const blob = await new Promise(resolve => {
               capturedCanvas.toBlob(resolve, 'image/png', 1.0)
             })
             const arrayBuffer = await blob.arrayBuffer()
             
             capturedChartImages.push({
               canvas: capturedCanvas,
               dataUrl,
               blob,
               arrayBuffer,
               title: `Chart ${capturedChartImages.length + 1}`,
               canvasElement: canvas,
               chartIndex: capturedChartImages.length,
               source: 'html2canvas-container',
               isHighResolution: true
             })
             
             console.log(`✅ Successfully captured container ${i} with html2canvas`)
           }
         } catch (error) {
           console.error(`❌ html2canvas container capture failed ${i}:`, error)
         }
       }
     }
   }
    
    console.log(`📊 Final result: Captured ${capturedChartImages.length} out of ${expectedChartCount} expected charts`)
     
     // Add fallback text if still no charts captured
    if (capturedChartImages.length === 0) {
      console.log('⚠️ No charts captured after all methods, adding text fallback')
      for (let i = 0; i < Math.max(expectedChartCount, 1); i++) {
        capturedChartImages.push({
          dataUrl: null,
          title: `Data Visualization ${i + 1}`,
          isTextFallback: true,
          fallbackText: language === 'id' ? 'Grafik tidak dapat ditampilkan dalam dokumen ini. Silakan lihat hasil penelitian di aplikasi untuk melihat visualisasi data.' : 'Chart cannot be displayed in this document. Please view the research results in the application to see data visualizations.',
          chartIndex: `fallback-${i}`,
          source: 'text-fallback'
        })
      }
    }
    
    return capturedChartImages
    } catch (error) {
      console.error('❌ Error during chart capture:', error)
      return []
    } finally {
      // Always clear the capturing flag
      setIsCapturingCharts(false)
      
      // CRITICAL: Enhanced registry preservation and backup management
      console.log('🔒 Chart capture completed, implementing enhanced registry preservation...')
      console.log('📊 Final registry size:', chartInstancesRegistry.current.size)
      console.log('💾 Current backup size:', chartRegistryBackup.current.size)
      
      // CRITICAL FIX: Always ensure backup is comprehensive and up-to-date
      if (chartInstancesRegistry.current.size > 0) {
        // Create a deep backup with enhanced metadata
        const enhancedBackup = new Map()
        for (const [id, entry] of chartInstancesRegistry.current.entries()) {
          enhancedBackup.set(id, {
            ...entry,
            backupTimestamp: Date.now(),
            captureReady: true,
            preservedForFutureDownloads: true
          })
        }
        chartRegistryBackup.current = enhancedBackup
        console.log('💾 Enhanced backup registry created with', chartRegistryBackup.current.size, 'entries')
        
        // Mark first download as completed to protect registry
        hasCompletedFirstDownload.current = true
        console.log('✅ First download marked as completed - registry protection activated')
      } else if (chartRegistryBackup.current.size > 0) {
        // If current registry is empty but backup exists, restore it immediately
        console.log('🔄 Current registry empty, performing emergency restoration from backup...')
        chartInstancesRegistry.current = new Map(chartRegistryBackup.current)
        console.log('✅ Emergency registry restoration completed:', chartInstancesRegistry.current.size)
        
        // Ensure chart refs are also restored
        chartRegistryBackup.current.forEach((entry, key) => {
          if (entry.canvas && entry.instance) {
            chartRefs.current[`restored-${key}`] = entry.instance
            chartRefs.current[`restored-canvas-${key}`] = entry.canvas
          }
        })
        console.log('✅ Chart refs also restored from backup')
      }
      
      // Final validation
      console.log('🔍 Final registry state:', {
        registrySize: chartInstancesRegistry.current.size,
        backupSize: chartRegistryBackup.current.size,
        hasCompletedFirstDownload: hasCompletedFirstDownload.current,
        chartRefsCount: Object.keys(chartRefs.current).length
      })
    }
  }

  // Get headings based on language
  const getHeadings = () => {
    if (language === 'en') {
      return {
        comprehensiveResearchPaper: 'Comprehensive_Research_Paper',
        generatedOn: 'Generated on',
        chart: 'Chart',
        dataVisualizationDescription: 'Data visualization showing research findings',
        tableOfContents: 'Table of Contents',
        abstract: 'Abstract',
        background: 'Background',
        objective: 'Objective', 
        methods: 'Methods',
        results: 'Results',
        conclusion: 'Conclusion',
        introduction: 'Introduction',
        introBackground: 'Background',
        problemStatement: 'Problem Statement',
        researchObjectives: 'Research Objectives',
        significance: 'Significance of the Study',
        literatureReview: 'Literature Review',
        theoreticalFramework: 'Theoretical Framework',
        previousStudies: 'Previous Studies',
        researchGap: 'Research Gap',
        methodology: 'Methodology',
        researchDesign: 'Research Design',
        dataCollection: 'Data Collection',
        dataAnalysis: 'Data Analysis',
        resultsSection: 'Results',
        descriptiveStatistics: 'Descriptive Statistics',
        dataVisualizations: 'Data Visualizations',
        discussion: 'Discussion',
        interpretation: 'Interpretation of Results',
        theoreticalImplications: 'Theoretical Implications',
        practicalImplications: 'Practical Implications',
        limitations: 'Limitations',
        conclusionSection: 'Conclusion',
        summary: 'Summary',
        contributions: 'Contributions',
        recommendations: 'Recommendations',
        references: 'References'
      }
    } else {
      return {
        comprehensiveResearchPaper: 'Makalah_Penelitian_Komprehensif',
        generatedOn: 'Dibuat pada',
        chart: 'Grafik',
        dataVisualizationDescription: 'Visualisasi data yang menunjukkan temuan penelitian',
        tableOfContents: 'Daftar Isi',
        abstract: 'Abstrak',
        background: 'Latar Belakang',
        objective: 'Tujuan',
        methods: 'Metode',
        results: 'Hasil',
        conclusion: 'Kesimpulan',
        introduction: 'Pendahuluan',
        introBackground: 'Latar Belakang',
        problemStatement: 'Rumusan Masalah',
        researchObjectives: 'Tujuan Penelitian',
        significance: 'Manfaat Penelitian',
        literatureReview: 'Tinjauan Pustaka',
        theoreticalFramework: 'Kerangka Teori',
        previousStudies: 'Penelitian Terdahulu',
        researchGap: 'Kesenjangan Penelitian',
        methodology: 'Metodologi',
        researchDesign: 'Desain Penelitian',
        dataCollection: 'Pengumpulan Data',
        dataAnalysis: 'Analisis Data',
        resultsSection: 'Hasil Penelitian',
        descriptiveStatistics: 'Statistik Deskriptif',
        dataVisualizations: 'Visualisasi Data',
        discussion: 'Pembahasan',
        interpretation: 'Interpretasi Hasil',
        theoreticalImplications: 'Implikasi Teoritis',
        practicalImplications: 'Implikasi Praktis',
        limitations: 'Keterbatasan',
        conclusionSection: 'Kesimpulan',
        summary: 'Ringkasan',
        contributions: 'Kontribusi',
        recommendations: 'Rekomendasi',
        references: 'Daftar Pustaka'
      }
    }
  }





  // Generate Word document
  const generateDOC = async () => {
    setIsGeneratingDOC(true)
    setGenerationProgress(0)
    try {
      // Capture all charts first
      setGenerationProgress(10)
      const chartImages = await captureAllCharts()
      setGenerationProgress(20)
      
      setGenerationProgress(30)
        const detailedContent = generateDetailedContent(effectiveResearchPaper)
        const headings = getHeadings()
        setGenerationProgress(40)
      
      const doc = new Document({
        sections: [{
          properties: {},
          children: [
            // Title
            new Paragraph({
              children: [new TextRun({ text: detailedContent?.title || (language === 'id' ? 'Makalah Penelitian' : 'Research Paper'), bold: true, size: 32 })],
              heading: HeadingLevel.TITLE,
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 }
            }),
            
            // Abstract Section
            new Paragraph({
              children: [new TextRun({ text: headings.abstract, bold: true, size: 24 })],
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: headings.background, bold: true }), new TextRun(detailedContent?.abstract?.background || (language === 'id' ? 'Informasi latar belakang tidak tersedia' : 'No background information available'))],
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: headings.objective, bold: true }), new TextRun(detailedContent?.abstract?.objective || (language === 'id' ? 'Informasi tujuan tidak tersedia' : 'No objective information available'))],
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: headings.methods, bold: true }), new TextRun(detailedContent?.abstract?.methods || (language === 'id' ? 'Informasi metode tidak tersedia' : 'No methods information available'))],
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: headings.results, bold: true }), new TextRun(detailedContent?.abstract?.results || (language === 'id' ? 'Informasi hasil tidak tersedia' : 'No results information available'))],
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: headings.conclusion, bold: true }), new TextRun(detailedContent?.abstract?.conclusion || (language === 'id' ? 'Informasi kesimpulan tidak tersedia' : 'No conclusion information available'))],
              spacing: { after: 400 }
            }),
            
            // Introduction Section
            new Paragraph({
              children: [new TextRun({ text: `1. ${headings.introduction}`, bold: true, size: 24 })],
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: `1.1 ${headings.introBackground}`, bold: true, size: 16 })],
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            }),
            new Paragraph({
              children: [new TextRun(detailedContent?.introduction?.background || (language === 'id' ? 'Informasi latar belakang tidak tersedia' : 'No background information available'))],
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: `1.2 ${headings.problemStatement}`, bold: true, size: 16 })],
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            }),
            new Paragraph({
              children: [new TextRun(detailedContent?.introduction?.problemStatement || (language === 'id' ? 'Pernyataan masalah tidak tersedia' : 'No problem statement available'))],
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: `1.3 ${headings.researchObjectives}`, bold: true, size: 16 })],
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            }),
            ...(Array.isArray(detailedContent?.introduction?.objectives) ? detailedContent.introduction.objectives.map(obj => {
              const objText = typeof obj === 'string' ? obj : (obj?.text || obj?.title || String(obj))
              return new Paragraph({
                children: [new TextRun(`• ${objText}`)],
                spacing: { after: 100 }
              })
            }) : [new Paragraph({
              children: [new TextRun(language === 'id' ? 'Tujuan penelitian tidak tersedia' : 'No research objectives available')],
              spacing: { after: 100 }
            })]),
            new Paragraph({
              children: [new TextRun({ text: `1.4 ${headings.significance}`, bold: true, size: 16 })],
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            }),
            new Paragraph({
              children: [new TextRun(detailedContent?.introduction?.significance || (language === 'id' ? 'Informasi signifikansi tidak tersedia' : 'No significance information available'))],
              spacing: { after: 400 }
            }),
            
            // Literature Review Section
            new Paragraph({
              children: [new TextRun({ text: `2. ${headings.literatureReview}`, bold: true, size: 24 })],
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: `2.1 ${headings.theoreticalFramework}`, bold: true, size: 16 })],
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            }),
            new Paragraph({
              children: [new TextRun(effectiveResearchPaper.literatureReview?.theoreticalFramework || (language === 'id' ? 'Bagian ini memberikan gambaran komprehensif tentang fondasi teoretis yang mendasari penelitian ini.' : 'This section provides a comprehensive overview of the theoretical foundations that underpin this research.'))],
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: `2.2 ${headings.previousStudies}`, bold: true, size: 16 })],
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            }),
            // Handle previousStudies - check if it's an array of objects or a string
            ...((() => {
              const previousStudiesContent = effectiveResearchPaper.literatureReview?.previousStudies
              if (Array.isArray(previousStudiesContent) && previousStudiesContent.length > 0) {
                // If it's an array of study objects, format them properly
                return previousStudiesContent.map((study, index) => {
                  if (study && typeof study === 'object') {
                    const author = study.author || (language === 'id' ? 'Penulis tidak diketahui' : 'Unknown author')
                    const year = study.year || ''
                    const title = study.title || (language === 'id' ? 'Judul tidak tersedia' : 'Title not available')
                    const yearText = year ? ` (${year})` : ''
                    return new Paragraph({
                      children: [new TextRun(`${index + 1}. ${author}${yearText}. ${title}`)],
                      spacing: { after: 100 }
                    })
                  } else if (typeof study === 'string') {
                    return new Paragraph({
                      children: [new TextRun(`${index + 1}. ${study}`)],
                      spacing: { after: 100 }
                    })
                  }
                  return null
                }).filter(Boolean)
              } else if (typeof previousStudiesContent === 'string') {
                // If it's already a string, use it directly
                return [new Paragraph({
                  children: [new TextRun(previousStudiesContent)],
                  spacing: { after: 200 }
                })]
              } else {
                // Fallback text if no previous studies data
                return [new Paragraph({
                  children: [new TextRun(language === 'id' ? 'Tinjauan ekstensif terhadap penelitian sebelumnya mengungkapkan kekayaan literatur.' : 'An extensive review of previous research reveals a rich body of literature.')],
                  spacing: { after: 200 }
                })]
              }
            })()),
            new Paragraph({
              children: [new TextRun({ text: `2.3 ${headings.researchGap}`, bold: true, size: 16 })],
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            }),
            new Paragraph({
              children: [new TextRun(effectiveResearchPaper.literatureReview?.researchGap || (language === 'id' ? 'Meskipun ada kemajuan signifikan di bidang ini, beberapa kesenjangan kritis masih tetap ada.' : 'Despite significant advances in the field, several critical gaps remain.'))],
              spacing: { after: 400 }
            }),
            
            // Methodology Section
            new Paragraph({
              children: [new TextRun({ text: `3. ${headings.methodology}`, bold: true, size: 24 })],
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: `3.1 ${headings.researchDesign}`, bold: true, size: 16 })],
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            }),
            new Paragraph({
              children: [new TextRun(effectiveResearchPaper.methodology?.researchDesign || (language === 'id' ? 'Penelitian ini menggunakan desain metode campuran yang komprehensif.' : 'This research employs a comprehensive mixed-methods design.'))],
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: `3.2 ${headings.dataCollection}`, bold: true, size: 16 })],
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            }),
            new Paragraph({
              children: [new TextRun(effectiveResearchPaper.methodology?.dataCollection || (language === 'id' ? 'Prosedur pengumpulan data dirancang untuk memastikan cakupan komprehensif.' : 'Data collection procedures were designed to ensure comprehensive coverage.'))],
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: `3.3 ${headings.dataAnalysis}`, bold: true, size: 16 })],
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            }),
            new Paragraph({
              children: [new TextRun(effectiveResearchPaper.methodology?.dataAnalysis || (language === 'id' ? 'Analisis data menggunakan pendekatan kuantitatif dan kualitatif.' : 'Data analysis employed both quantitative and qualitative approaches.'))],
              spacing: { after: 400 }
            }),
            
            // Results Section
            new Paragraph({
              children: [new TextRun({ text: `4. ${headings.resultsSection}`, bold: true, size: 24 })],
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: `4.1 ${headings.descriptiveStatistics}`, bold: true, size: 16 })],
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            }),
            new Paragraph({
              children: [new TextRun(effectiveResearchPaper.results?.descriptiveStatistics || (language === 'id' ? 'Statistik deskriptif memberikan gambaran umum tentang karakteristik kunci dari dataset.' : 'The descriptive statistics provide an overview of the key characteristics of the dataset.'))],
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: `4.2 ${headings.dataVisualizations}`, bold: true, size: 16 })],
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            }),
            // Add chart images and descriptions for DOC
            ...((() => {
              const researchPaperForCharts = getResearchPaperForCharts()
              return researchPaperForCharts.results?.dataVisualization && Array.isArray(researchPaperForCharts.results.dataVisualization) ? 
                researchPaperForCharts.results.dataVisualization.map((chart, index) => {
                const chartTitle = String(chart?.title || `Chart ${index + 1}`)
                const chartDescription = String(chart?.description || (headings.dataVisualizationDescription || (language === 'id' ? 'Grafik visualisasi data' : 'Data visualization chart')))
                const chartData = chart?.data
                let dataSummary = ''
                
                if (chartData && chartData.labels && chartData.values) {
                  dataSummary = `${language === 'id' ? 'Ringkasan Data:' : 'Data Summary:'} ${chartData.labels.map((label, i) => {
                    const value = chartData.values[i]
                    const displayValue = typeof value === 'object' ? JSON.stringify(value) : (value || 0)
                    return `${label}: ${displayValue}`
                  }).join(', ')}`
                }
                
                const elements = [
                  new Paragraph({
                    children: [new TextRun({ text: chartTitle, bold: true })],
                    spacing: { before: 100, after: 50 }
                  })
                ]
                
                // Add high-resolution chart image with improved error handling
                if (chartImages[index]) {
                  if (chartImages[index].arrayBuffer && !chartImages[index].isTextFallback) {
                    try {
                      // Use larger dimensions for better visibility and quality
                      const imageWidth = 500 // Increased from 400
                      const imageHeight = 375 // Increased from 300, maintaining aspect ratio
                      
                      elements.push(
                        new Paragraph({
                          children: [
                            new ImageRun({
                              data: chartImages[index].arrayBuffer,
                              transformation: {
                                width: imageWidth,
                                height: imageHeight
                              }
                            })
                          ],
                          alignment: AlignmentType.CENTER,
                          spacing: { before: 100, after: 100 } // More spacing around images
                        })
                      )
                      console.log(`✅ Successfully added high-resolution chart image ${index} to DOC (${imageWidth}x${imageHeight})`)
                    } catch (error) {
                      console.error('Error adding chart image to DOC:', error)
                      elements.push(
                        new Paragraph({
                          children: [new TextRun({ text: `[${headings.chart || 'Chart'} Image: ${chartTitle} - ${language === 'id' ? 'Gambar resolusi tinggi tidak dapat disematkan' : 'High-resolution image could not be embedded'}]`, italic: true })],
                          spacing: { after: 50 }
                        })
                      )
                    }
                  } else {
                    // Text fallback for charts that couldn't be captured
                    elements.push(
                      new Paragraph({
                        children: [new TextRun({ text: `[${headings.chart || 'Chart'}: ${chartTitle} - ${language === 'id' ? 'Representasi visual tidak tersedia' : 'Visual representation not available'}]`, italic: true })],
                        spacing: { after: 50 }
                      })
                    )
                    console.log(`📝 Added text fallback for chart ${index} to DOC`)
                  }
                } else {
                  elements.push(
                    new Paragraph({
                      children: [new TextRun({ text: `[${headings.chart || 'Chart'}: ${chartTitle} - ${language === 'id' ? 'Tidak tersedia' : 'Not available'}]`, italic: true })],
                      spacing: { after: 50 }
                    })
                  )
                }
                
                elements.push(
                  new Paragraph({
                    children: [new TextRun(chartDescription)],
                    spacing: { after: 50 }
                  })
                )
                
                if (dataSummary) {
                  elements.push(
                    new Paragraph({
                      children: [new TextRun(dataSummary)],
                      spacing: { after: 100 }
                    })
                  )
                }
                
                return elements
              }).flat() : []
            })()),
            
            // Discussion Section
            new Paragraph({
              children: [new TextRun({ text: `5. ${headings.discussion}`, bold: true, size: 24 })],
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: `5.1 ${headings.interpretation}`, bold: true, size: 16 })],
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            }),
            new Paragraph({
              children: [new TextRun(effectiveResearchPaper.discussion?.interpretation || (language === 'id' ? 'Hasil diinterpretasikan dalam konteks pengetahuan yang ada.' : 'The results are interpreted in the context of existing knowledge.'))],
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: `5.2 ${headings.theoreticalImplications}`, bold: true, size: 16 })],
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            }),
            new Paragraph({
              children: [new TextRun(effectiveResearchPaper.discussion?.theoreticalImplications || (language === 'id' ? 'Penelitian ini berkontribusi pada pemahaman teoretis.' : 'The research contributes to theoretical understanding.'))],
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: `5.3 ${headings.practicalImplications}`, bold: true, size: 16 })],
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            }),
            new Paragraph({
              children: [new TextRun(effectiveResearchPaper.discussion?.practicalImplications || (language === 'id' ? 'Temuan ini memiliki implikasi praktis yang penting.' : 'The findings have important practical implications.'))],
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: `5.4 ${headings.limitations}`, bold: true, size: 16 })],
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            }),
            new Paragraph({
              children: [new TextRun(effectiveResearchPaper.discussion?.limitations || (language === 'id' ? 'Beberapa keterbatasan harus diakui.' : 'Several limitations should be acknowledged.'))],
              spacing: { after: 400 }
            }),
            
            // Conclusion Section
            new Paragraph({
              children: [new TextRun({ text: `6. ${headings.conclusionSection}`, bold: true, size: 24 })],
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: `6.1 ${headings.summary}`, bold: true, size: 16 })],
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            }),
            new Paragraph({
              children: [new TextRun(effectiveResearchPaper.conclusion?.summary || (language === 'id' ? 'Penelitian komprehensif ini telah memberikan wawasan yang signifikan.' : 'This comprehensive research has provided significant insights.'))],
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: `6.2 ${headings.contributions}`, bold: true, size: 16 })],
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            }),
            new Paragraph({
              children: [new TextRun(effectiveResearchPaper.conclusion?.contributions || (language === 'id' ? 'Penelitian ini memberikan beberapa kontribusi penting.' : 'This research makes several important contributions.'))],
              spacing: { after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: `6.3 ${headings.recommendations}`, bold: true, size: 16 })],
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            }),
            // Handle recommendations array properly
            ...(effectiveResearchPaper.conclusion?.recommendations && Array.isArray(effectiveResearchPaper.conclusion.recommendations) ? 
              effectiveResearchPaper.conclusion.recommendations.map(rec => {
                const recText = typeof rec === 'string' ? rec : (rec?.text || rec?.title || String(rec))
                return new Paragraph({
                  children: [new TextRun(`• ${recText}`)],
                  spacing: { after: 100 }
                })
              }) : []),
            
            // References Section
            new Paragraph({
              children: [new TextRun({ text: `7. ${headings.references}`, bold: true, size: 24 })],
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 }
            }),
            // Handle academic sources array properly
            ...(effectiveResearchPaper.references?.academicSources && Array.isArray(effectiveResearchPaper.references.academicSources) ? 
              effectiveResearchPaper.references.academicSources.map((source, index) => {
                const sourceText = typeof source === 'string' ? source : 
                  (source?.citation || source?.title || `${language === 'id' ? 'Referensi' : 'Reference'} ${index + 1}`)
                return new Paragraph({
                  children: [new TextRun(`${index + 1}. ${sourceText}`)],
                  spacing: { after: 100 }
                })
              }) : [])
          ]
        }]
      })
      
      setGenerationProgress(80)
      const blob = await Packer.toBlob(doc)
      setGenerationProgress(90)
      
      const safeTitle = String(effectiveResearchPaper?.title || (language === 'id' ? 'Makalah_Penelitian' : 'Research_Paper')).replace(/[^a-zA-Z0-9]/g, '_')
      saveAs(blob, `${headings.comprehensiveResearchPaper}_${safeTitle}.docx`)
      
      // Note: Research result is already saved to history in index.jsx
      // No need to save again here to avoid duplication
      
      setGenerationProgress(100)
      
      // Mark first download as completed to preserve chart registry
      hasCompletedFirstDownload.current = true
      console.log('✅ First download completed - chart registry is now protected from clearing')
      
      // Ensure backup registry is updated after successful DOC generation
      if (chartInstancesRegistry.current.size > 0) {
        chartRegistryBackup.current = new Map(chartInstancesRegistry.current)
        console.log('💾 Updated backup registry after DOC generation:', chartRegistryBackup.current.size, 'entries')
      }
      
    } catch (error) {
      console.error('Error generating Word document:', error)
      alert(language === 'id' ? 'Error saat membuat dokumen Word' : 'Error generating Word document')
    } finally {
      setIsGeneratingDOC(false)
      setGenerationProgress(0)
    }
  }

  // Generate comprehensive PDF
  const generatePDF = async () => {
    setIsGeneratingPDF(true)
    setGenerationProgress(0)
    try {
      // Capture all charts first
      setGenerationProgress(10)
      const chartImages = await captureAllCharts()
      setGenerationProgress(20)
      
      const detailedContent = generateDetailedContent(effectiveResearchPaper)
      const pdf = new jsPDF()
      let yPosition = 20
      const pageHeight = pdf.internal.pageSize.height
      const margin = 20
      const lineHeight = 6
      
      // Helper function to add new page if needed
      const checkPageBreak = (requiredSpace = 20) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          pdf.addPage()
          yPosition = margin
        }
      }
      
      // Helper function to add text with automatic page breaks
      const addTextWithPageBreak = (text, fontSize = 12, fontStyle = 'normal', maxWidth = 170) => {
        pdf.setFontSize(fontSize)
        pdf.setFont('helvetica', fontStyle)
        // Ensure text is a string and handle null/undefined values
        const textString = String(text || '')
        const splitText = pdf.splitTextToSize(textString, maxWidth)
        
        for (let i = 0; i < splitText.length; i++) {
          checkPageBreak(lineHeight)
          pdf.text(splitText[i], margin, yPosition)
          yPosition += lineHeight
        }
        yPosition += 5 // Extra spacing after text block
      }
      
      // Title Page
      pdf.setFontSize(24)
      pdf.setFont('helvetica', 'bold')
      const titleString = String(detailedContent?.title || (language === 'id' ? 'Makalah Penelitian' : 'Research Paper'))
      const titleLines = pdf.splitTextToSize(titleString, 170)
      titleLines.forEach(line => {
        pdf.text(line, margin, yPosition)
        yPosition += 10
      })
      yPosition += 20
      
      // Get headings first
      const headings = getHeadings()
      
      pdf.setFontSize(14)
      pdf.setFont('helvetica', 'normal')
      pdf.text(headings.comprehensiveResearchPaper, margin, yPosition)
      yPosition += 10
      pdf.text(`${headings.generatedOn || 'Generated on'}: ${new Date().toLocaleDateString()}`, margin, yPosition)
      yPosition += 30
      
      // Table of Contents
      checkPageBreak(50)
      addTextWithPageBreak(headings.tableOfContents, 18, 'bold')
      yPosition += 10
      
      const tocItems = [
        headings.abstract,
        `1. ${headings.introduction}`,
        `   1.1 ${headings.introBackground}`,
        `   1.2 ${headings.problemStatement}`, 
        `   1.3 ${headings.researchObjectives}`,
        `   1.4 ${headings.significance}`,
        `2. ${headings.literatureReview}`,
        `   2.1 ${headings.theoreticalFramework}`,
        `   2.2 ${headings.previousStudies}`,
        `   2.3 ${headings.researchGap}`,
        `3. ${headings.methodology}`,
        `   3.1 ${headings.researchDesign}`,
        `   3.2 ${headings.dataCollection}`,
        `   3.3 ${headings.dataAnalysis}`,
        `4. ${headings.resultsSection}`,
        `   4.1 ${headings.descriptiveStatistics}`,
        `   4.2 ${headings.dataVisualizations}`,
        `5. ${headings.discussion}`,
        `   5.1 ${headings.interpretation}`,
        `   5.2 ${headings.theoreticalImplications}`,
        `   5.3 ${headings.practicalImplications}`,
        `   5.4 ${headings.limitations}`,
        `6. ${headings.conclusionSection}`,
        `   6.1 ${headings.summary}`,
        `   6.2 ${headings.contributions}`,
        `   6.3 ${headings.recommendations}`,
        `7. ${headings.references}`
      ]
      
      tocItems.forEach(item => {
        checkPageBreak()
        pdf.setFontSize(12)
        pdf.setFont('helvetica', 'normal')
        pdf.text(item, margin, yPosition)
        yPosition += lineHeight
      })
      
      // Start new page for content
      pdf.addPage()
      yPosition = margin
      
      // Abstract Section
      addTextWithPageBreak(headings.abstract, 18, 'bold')
      yPosition += 5
      
      addTextWithPageBreak(headings.background, 14, 'bold')
      addTextWithPageBreak(detailedContent?.abstract?.background || (language === 'id' ? 'Informasi latar belakang tidak tersedia' : 'No background information available'))
      
      addTextWithPageBreak(headings.objective, 14, 'bold')
      addTextWithPageBreak(detailedContent?.abstract?.objective || (language === 'id' ? 'Informasi tujuan tidak tersedia' : 'No objective information available'))
      
      addTextWithPageBreak(headings.methods, 14, 'bold')
      addTextWithPageBreak(detailedContent?.abstract?.methods || (language === 'id' ? 'Informasi metode tidak tersedia' : 'No methods information available'))
      
      addTextWithPageBreak(headings.results, 14, 'bold')
      addTextWithPageBreak(detailedContent?.abstract?.results || (language === 'id' ? 'Informasi hasil tidak tersedia' : 'No results information available'))
      
      addTextWithPageBreak(headings.conclusion, 14, 'bold')
      addTextWithPageBreak(detailedContent?.abstract?.conclusion || (language === 'id' ? 'Informasi kesimpulan tidak tersedia' : 'No conclusion information available'))
      
      // Introduction Section
      checkPageBreak(30)
      addTextWithPageBreak(`1. ${headings.introduction}`, 18, 'bold')
      yPosition += 5
      
      addTextWithPageBreak(`1.1 ${headings.introBackground}`, 14, 'bold')
      addTextWithPageBreak(detailedContent?.introduction?.background || (language === 'id' ? 'Informasi latar belakang tidak tersedia' : 'No background information available'))
      
      addTextWithPageBreak(`1.2 ${headings.problemStatement}`, 14, 'bold')
      addTextWithPageBreak(detailedContent?.introduction?.problemStatement || (language === 'id' ? 'Pernyataan masalah tidak tersedia' : 'No problem statement available'))
      
      addTextWithPageBreak(`1.3 ${headings.researchObjectives}`, 14, 'bold')
      if (Array.isArray(detailedContent?.introduction?.objectives)) {
        detailedContent.introduction.objectives.forEach((objective, index) => {
          const objText = typeof objective === 'string' ? objective : (objective?.text || objective?.title || String(objective))
          addTextWithPageBreak(`${index + 1}. ${objText}`)
        })
      } else {
        addTextWithPageBreak(language === 'id' ? 'Tujuan penelitian tidak tersedia' : 'No research objectives available')
      }
      
      addTextWithPageBreak(`1.4 ${headings.significance}`, 14, 'bold')
      addTextWithPageBreak(detailedContent?.introduction?.significance || (language === 'id' ? 'Informasi signifikansi tidak tersedia' : 'No significance information available'))
      
      // Literature Review Section
      checkPageBreak(30)
      addTextWithPageBreak(`2. ${headings.literatureReview}`, 18, 'bold')
      yPosition += 5
      
      addTextWithPageBreak(`2.1 ${headings.theoreticalFramework}`, 14, 'bold')
      addTextWithPageBreak(effectiveResearchPaper.literatureReview?.theoreticalFramework || (language === 'id' ? 'Bagian ini memberikan gambaran komprehensif tentang fondasi teoretis yang mendasari penelitian ini. Kerangka teoretis mengintegrasikan berbagai perspektif disiplin untuk menciptakan fondasi yang kuat dalam memahami fenomena kompleks yang diteliti.' : 'This section provides a comprehensive overview of the theoretical foundations that underpin this research. The theoretical framework integrates multiple disciplinary perspectives to create a robust foundation for understanding the complex phenomena under investigation.'))
      
      addTextWithPageBreak(`2.2 ${headings.previousStudies}`, 14, 'bold')
      
      // Handle previousStudies - check if it's an array of objects or a string
      const previousStudiesContent = effectiveResearchPaper.literatureReview?.previousStudies
      if (Array.isArray(previousStudiesContent) && previousStudiesContent.length > 0) {
        // If it's an array of study objects, format them properly
        previousStudiesContent.forEach((study, index) => {
          if (study && typeof study === 'object') {
            const author = study.author || (language === 'id' ? 'Penulis tidak diketahui' : 'Unknown author')
            const year = study.year || ''
            const title = study.title || (language === 'id' ? 'Judul tidak tersedia' : 'Title not available')
            const yearText = year ? ` (${year})` : ''
            addTextWithPageBreak(`${index + 1}. ${author}${yearText}. ${title}`)
          } else if (typeof study === 'string') {
            addTextWithPageBreak(`${index + 1}. ${study}`)
          }
        })
      } else if (typeof previousStudiesContent === 'string') {
        // If it's already a string, use it directly
        addTextWithPageBreak(previousStudiesContent)
      } else {
        // Fallback text if no previous studies data
        addTextWithPageBreak(language === 'id' ? 'Tinjauan ekstensif terhadap penelitian sebelumnya mengungkapkan kekayaan literatur yang telah berkontribusi pada pemahaman bidang saat ini. Studi-studi dasar awal menetapkan prinsip-prinsip dasar dan pendekatan metodologis, sementara investigasi yang lebih baru telah memperbaiki pemahaman teoretis dan memperluas aplikasi praktis.' : 'An extensive review of previous research reveals a rich body of literature that has contributed to current understanding of the field. Early foundational studies established basic principles and methodological approaches, while more recent investigations have refined theoretical understanding and expanded practical applications.')
      }
      
      addTextWithPageBreak(`2.3 ${headings.researchGap}`, 14, 'bold')
      addTextWithPageBreak(effectiveResearchPaper.literatureReview?.researchGap || (language === 'id' ? 'Meskipun ada kemajuan signifikan di bidang ini, beberapa kesenjangan kritis masih tetap ada dalam pengetahuan dan pemahaman saat ini. Kesenjangan ini merupakan peluang untuk kontribusi yang bermakna baik untuk pengembangan teoretis maupun aplikasi praktis.' : 'Despite significant advances in the field, several critical gaps remain in current knowledge and understanding. These gaps represent opportunities for meaningful contribution to both theoretical development and practical application.'))
      
      // Methodology Section
      checkPageBreak(30)
      addTextWithPageBreak(`3. ${headings.methodology}`, 18, 'bold')
      yPosition += 5
      
      addTextWithPageBreak(`3.1 ${headings.researchDesign}`, 14, 'bold')
      addTextWithPageBreak(effectiveResearchPaper.methodology?.researchDesign || (language === 'id' ? 'Penelitian ini menggunakan desain metode campuran yang komprehensif yang menggabungkan pendekatan kuantitatif dan kualitatif untuk memastikan temuan yang kuat dan dapat diandalkan. Desain penelitian disusun untuk mengatasi kompleksitas pertanyaan penelitian sambil mempertahankan ketelitian metodologis.' : 'This research employs a comprehensive mixed-methods design that combines quantitative and qualitative approaches to ensure robust and reliable findings. The research design is structured to address the complexity of the research questions while maintaining methodological rigor.'))
      
      addTextWithPageBreak(`3.2 ${headings.dataCollection}`, 14, 'bold')
      addTextWithPageBreak(effectiveResearchPaper.methodology?.dataCollection || (language === 'id' ? 'Prosedur pengumpulan data dirancang untuk memastikan cakupan komprehensif domain penelitian sambil mempertahankan standar tinggi reliabilitas dan validitas. Berbagai metode pengumpulan data digunakan, termasuk survei terstruktur, wawancara mendalam, studi observasional, dan analisis dokumen.' : 'Data collection procedures were designed to ensure comprehensive coverage of the research domain while maintaining high standards of reliability and validity. Multiple data collection methods were employed, including structured surveys, in-depth interviews, observational studies, and document analysis.'))
      
      addTextWithPageBreak(`3.3 ${headings.dataAnalysis}`, 14, 'bold')
      addTextWithPageBreak(effectiveResearchPaper.methodology?.dataAnalysis || (language === 'id' ? 'Prosedur analisis data menggabungkan teknik statistik kuantitatif dan pendekatan analitis kualitatif. Analisis kuantitatif mencakup statistik deskriptif, pengujian inferensial, dan teknik analisis multivariat. Analisis kualitatif menggunakan pendekatan analisis tematik, analisis konten, dan analisis naratif.' : 'Data analysis procedures incorporated both quantitative statistical techniques and qualitative analytical approaches. Quantitative analysis included descriptive statistics, inferential testing, and multivariate analysis techniques. Qualitative analysis employed thematic analysis, content analysis, and narrative analysis approaches.'))
      
      // Results Section
      checkPageBreak(30)
      addTextWithPageBreak(`4. ${headings.resultsSection}`, 18, 'bold')
      yPosition += 5
      
      addTextWithPageBreak(`4.1 ${headings.descriptiveStatistics}`, 14, 'bold')
      addTextWithPageBreak(effectiveResearchPaper.results?.descriptiveStatistics || (language === 'id' ? 'Analisis deskriptif mengungkapkan pola dan karakteristik penting dalam data. Temuan utama mencakup variasi signifikan di berbagai variabel dan konteks, dengan tren yang menonjol muncul dari analisis komprehensif. Statistik deskriptif memberikan dasar untuk memahami pola yang lebih luas.' : 'The descriptive analysis reveals important patterns and characteristics within the data. Key findings include significant variations across different variables and contexts, with notable trends emerging from the comprehensive analysis. The descriptive statistics provide a foundation for understanding the broader patterns.'))
      
      addTextWithPageBreak(`4.2 ${headings.dataVisualizations}`, 14, 'bold')
      
      // Add chart images, descriptions and data
      const researchPaperForCharts = getResearchPaperForCharts()
      if (researchPaperForCharts.results?.dataVisualization && Array.isArray(researchPaperForCharts.results.dataVisualization)) {
        researchPaperForCharts.results.dataVisualization.forEach((chart, index) => {
          if (chart && typeof chart === 'object') {
            const chartTitle = chart.title || `Chart ${index + 1}`
            const chartDescription = chart.description || (headings.dataVisualizationDescription || (language === 'id' ? 'Visualisasi data yang menunjukkan temuan penelitian' : 'Data visualization showing research findings'))
            const chartData = chart.data || {}
            
            // Add spacing and page break if needed
            checkPageBreak(30)
            
            addTextWithPageBreak(`${headings.chart || 'Chart'} ${index + 1}: ${chartTitle}`, 12, 'bold')
            
            // Add high-resolution chart image with improved handling
            if (chartImages[index]) {
              if (chartImages[index].dataUrl && !chartImages[index].isTextFallback) {
                checkPageBreak(180) // More space for larger, higher quality images
                try {
                  // Calculate optimal dimensions for high-resolution charts
                  const maxWidth = 160 // Increased width for better visibility
                  const maxHeight = 120 // Increased height for better visibility
                  
                  // Add the high-resolution chart image to the PDF
                  pdf.addImage(
                    chartImages[index].dataUrl, 
                    'PNG', 
                    margin, 
                    yPosition, 
                    maxWidth, 
                    maxHeight,
                    undefined, // alias
                    'FAST' // compression for better quality
                  )
                  yPosition += maxHeight + 15 // Move position after image with more spacing
                  console.log(`✅ Successfully added high-resolution chart image ${index} to PDF (${maxWidth}x${maxHeight})`)
                } catch (error) {
                  console.error('Error adding chart image to PDF:', error)
                  // Fallback: add text description if image fails
                  addTextWithPageBreak(`[${headings.chart || 'Chart'} Image: ${chartTitle} - ${language === 'id' ? 'Gambar resolusi tinggi tidak dapat dirender' : 'High-resolution image could not be rendered'}]`, 10, 'italic')
                }
              } else {
                // Text fallback for charts that couldn't be captured
                addTextWithPageBreak(`[${headings.chart || 'Chart'}: ${chartTitle} - ${language === 'id' ? 'Representasi visual tidak tersedia' : 'Visual representation not available'}]`, 10, 'italic')
                console.log(`📝 Added text fallback for chart ${index} to PDF`)
              }
            } else {
              addTextWithPageBreak(`[${headings.chart || 'Chart'}: ${chartTitle} - ${language === 'id' ? 'Tidak tersedia' : 'Not available'}]`, 10, 'italic')
            }
            
            addTextWithPageBreak(chartDescription)
            
            // Add chart data summary
            if (chartData.labels && chartData.values) {
              addTextWithPageBreak(language === 'id' ? 'Ringkasan Data:' : 'Data Summary:', 10, 'bold')
              chartData.labels.forEach((label, i) => {
                const value = chartData.values[i]
                const displayValue = typeof value === 'object' ? JSON.stringify(value) : (value || (language === 'id' ? 'T/A' : 'N/A'))
                addTextWithPageBreak(`• ${label}: ${displayValue}`, 10)
              })
            }
            addTextWithPageBreak('') // Add spacing
          }
        })
      } else {
        addTextWithPageBreak(language === 'id' ? 'Temuan penelitian didukung oleh visualisasi data komprehensif yang menggambarkan pola dan hubungan kunci. Visualisasi ini memberikan bukti yang jelas untuk kesimpulan penelitian dan memfasilitasi pemahaman hubungan kompleks dalam data.' : 'The research findings are supported by comprehensive data visualizations that illustrate key patterns and relationships. These visualizations provide clear evidence for the research conclusions and facilitate understanding of complex relationships within the data.')
      }
      
      // Discussion Section
      checkPageBreak(30)
      addTextWithPageBreak(`5. ${headings.discussion}`, 18, 'bold')
      yPosition += 5
      
      addTextWithPageBreak(`5.1 ${headings.interpretation}`, 14, 'bold')
      addTextWithPageBreak(effectiveResearchPaper.discussion?.interpretation || (language === 'id' ? 'Temuan penelitian memberikan wawasan yang signifikan terhadap fenomena yang diselidiki. Hasil menunjukkan pola dan hubungan yang jelas yang memajukan pemahaman teoretis dan pengetahuan praktis.' : 'The research findings provide significant insights into the phenomena under investigation. The results demonstrate clear patterns and relationships that advance both theoretical understanding and practical knowledge.'))
      
      addTextWithPageBreak(`5.2 ${headings.theoreticalImplications}`, 14, 'bold')
      addTextWithPageBreak(effectiveResearchPaper.discussion?.theoreticalImplications || (language === 'id' ? 'Temuan memiliki implikasi penting untuk pengembangan teoretis di bidang ini. Hasil mendukung kerangka teoretis yang ada sambil juga mengungkapkan wawasan baru yang memperluas pemahaman saat ini.' : 'The findings have important implications for theoretical development in the field. The results support existing theoretical frameworks while also revealing new insights that extend current understanding.'))
      
      addTextWithPageBreak(`5.3 ${headings.practicalImplications}`, 14, 'bold')
      addTextWithPageBreak(effectiveResearchPaper.discussion?.practicalImplications || (language === 'id' ? 'Temuan penelitian memiliki implikasi praktis yang signifikan bagi pemangku kepentingan di berbagai sektor. Hasil memberikan panduan berbasis bukti untuk meningkatkan praktik dan hasil di pengaturan dunia nyata.' : 'The research findings have significant practical implications for stakeholders across multiple sectors. The results provide evidence-based guidance for improving practices and outcomes in real-world settings.'))
      
      addTextWithPageBreak(`5.4 ${headings.limitations}`, 14, 'bold')
      addTextWithPageBreak(effectiveResearchPaper.discussion?.limitations || (language === 'id' ? 'Meskipun penelitian ini memberikan wawasan yang berharga, beberapa keterbatasan harus diakui. Keterbatasan ini memberikan konteks penting untuk menafsirkan temuan dan mengidentifikasi area untuk perhatian penelitian masa depan.' : 'While this research provides valuable insights, several limitations should be acknowledged. These limitations provide important context for interpreting the findings and identify areas for future research attention.'))
      
      // Conclusion Section
      checkPageBreak(30)
      addTextWithPageBreak(`6. ${headings.conclusionSection}`, 18, 'bold')
      yPosition += 5
      
      addTextWithPageBreak(`6.1 ${headings.summary}`, 14, 'bold')
      addTextWithPageBreak(effectiveResearchPaper.conclusion?.summary || (language === 'id' ? 'Penelitian komprehensif ini telah memberikan wawasan yang signifikan terhadap fenomena yang diselidiki. Temuan menunjukkan nilai pendekatan metodologis yang ketat dan kerangka analitis komprehensif untuk memajukan pemahaman di area kritis ini.' : 'This comprehensive research has provided significant insights into the phenomena under investigation. The findings demonstrate the value of rigorous methodological approaches and comprehensive analytical frameworks for advancing understanding in this critical area.'))
      
      addTextWithPageBreak(`6.2 ${headings.contributions}`, 14, 'bold')
      addTextWithPageBreak(effectiveResearchPaper.conclusion?.contributions || (language === 'id' ? 'Penelitian ini memberikan beberapa kontribusi penting untuk bidang ini. Kontribusi teoretis memajukan pemahaman tentang prinsip dan hubungan fundamental, sementara kontribusi metodologis meningkatkan kemampuan penelitian dan kecanggihan analitis.' : 'This research makes several important contributions to the field. The theoretical contributions advance understanding of fundamental principles and relationships, while the methodological contributions enhance research capabilities and analytical sophistication.'))
      
      addTextWithPageBreak(`6.3 ${headings.recommendations}`, 14, 'bold')
      if (effectiveResearchPaper.conclusion?.recommendations && Array.isArray(effectiveResearchPaper.conclusion.recommendations)) {
        effectiveResearchPaper.conclusion.recommendations.forEach((rec, index) => {
          const recText = typeof rec === 'string' ? rec : (rec?.text || rec?.title || String(rec))
          addTextWithPageBreak(`${index + 1}. ${recText}`)
        })
      } else {
        addTextWithPageBreak(language === 'id' ? 'Berdasarkan temuan penelitian, beberapa rekomendasi muncul untuk penelitian dan praktik masa depan. Rekomendasi ini memberikan panduan bagi pemangku kepentingan yang ingin menerapkan temuan penelitian dan mengidentifikasi prioritas untuk investigasi dan pengembangan berkelanjutan.' : 'Based on the research findings, several recommendations emerge for future research and practice. These recommendations provide guidance for stakeholders seeking to implement the research findings and identify priorities for continued investigation and development.')
      }
      
      // References Section
      checkPageBreak(30)
      addTextWithPageBreak(`7. ${headings.references}`, 18, 'bold')
      yPosition += 5
      
      if (academicSources && Array.isArray(academicSources) && academicSources.length > 0) {
        academicSources.forEach((source, index) => {
          const sourceTitle = typeof source === 'string' ? source : (source?.title || (language === 'id' ? 'Sumber Akademik' : 'Academic Source'))
          const sourceUrl = typeof source === 'object' && source?.url ? source.url : ''
          const urlText = sourceUrl ? (language === 'id' ? `. Diambil dari ${sourceUrl}` : `. Retrieved from ${sourceUrl}`) : ''
          addTextWithPageBreak(`${index + 1}. ${sourceTitle}${urlText}`)
        })
      } else {
        addTextWithPageBreak(language === 'id' ? 'Sumber akademik dan referensi yang mendukung penelitian ini tersedia atas permintaan. Tinjauan pustaka komprehensif menggabungkan publikasi yang telah ditinjau sejawat, sumber otoritatif, dan temuan penelitian terkini yang relevan dengan domain studi.' : 'Academic sources and references supporting this research are available upon request. The comprehensive literature review incorporates peer-reviewed publications, authoritative sources, and current research findings relevant to the study domain.')
      }
      
      // Save PDF
      setGenerationProgress(80)
      const safeTitle = String(effectiveResearchPaper?.title || (language === 'id' ? 'Makalah_Penelitian' : 'Research_Paper')).replace(/[^a-zA-Z0-9]/g, '_')
      pdf.save(`${headings.comprehensiveResearchPaper}_${safeTitle}.pdf`)
      setGenerationProgress(90)
      
      // Note: Research result is already saved to history in index.jsx
      // No need to save again here to avoid duplication
      
      setGenerationProgress(100)
      
      // Mark first download as completed to preserve chart registry
      hasCompletedFirstDownload.current = true
      console.log('✅ First download completed - chart registry is now protected from clearing')
      
      // Ensure backup registry is updated after successful PDF generation
      if (chartInstancesRegistry.current.size > 0) {
        chartRegistryBackup.current = new Map(chartInstancesRegistry.current)
        console.log('💾 Updated backup registry after PDF generation:', chartRegistryBackup.current.size, 'entries')
      }
      
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert(language === 'id' ? 'Error saat membuat PDF' : 'Error generating PDF')
    } finally {
      setIsGeneratingPDF(false)
      setGenerationProgress(0)
    }
  }

  // Copy text to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    alert(language === 'id' ? 'Teks berhasil disalin!' : 'Text copied to clipboard!')
  }

  // Toggle full text display
  const toggleFullText = (section) => {
    setShowFullText(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  // Truncate text
  const truncateText = (text, maxLength = 200) => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  const sections = [
    { id: 'abstract', name: language === 'id' ? 'Abstrak' : 'Abstract', icon: DocumentTextIcon },
    { id: 'introduction', name: language === 'id' ? 'Pendahuluan' : 'Introduction', icon: AcademicCapIcon },
    { id: 'literature', name: language === 'id' ? 'Tinjauan Pustaka' : 'Literature Review', icon: DocumentTextIcon },
    { id: 'methodology', name: language === 'id' ? 'Metodologi' : 'Methodology', icon: DocumentTextIcon },
    { id: 'results', name: language === 'id' ? 'Hasil' : 'Results', icon: ChartBarIcon },
    { id: 'discussion', name: language === 'id' ? 'Diskusi' : 'Discussion', icon: DocumentTextIcon },
    { id: 'conclusion', name: language === 'id' ? 'Kesimpulan' : 'Conclusion', icon: DocumentTextIcon },
    { id: 'references', name: language === 'id' ? 'Referensi' : 'References', icon: AcademicCapIcon }
  ]

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary">
      {/* Header */}
      <div className="bg-bg-secondary border-b border-border-primary sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
              <button
                onClick={onBack}
                className="flex items-center space-x-1 sm:space-x-2 text-text-tertiary hover:text-text-primary transition-colors flex-shrink-0"
              >
                <Search className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="font-medium text-sm sm:text-base">{language === 'id' ? 'Kembali' : 'Back'}</span>
              </button>
              <div className="h-4 sm:h-6 w-px bg-border-primary flex-shrink-0" />
              <div className="flex items-center space-x-1 sm:space-x-2 min-w-0">
                  <DocumentTextIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <h1 className="text-lg sm:text-xl font-bold text-text-primary truncate">Research Agent</h1>
                    {!isCompleted && (
                      <p className="text-xs sm:text-sm text-text-tertiary truncate">
                        {language === 'id' ? 'Sedang membuat research paper...' : 'Creating research paper...'}
                      </p>
                    )}
                  </div>
                </div>
            </div>
            
            {isCompleted && effectiveResearchPaper && (
              <div className="flex items-center space-x-1 sm:space-x-3 flex-shrink-0">
                <button
                  onClick={generatePDF}
                  disabled={isGeneratingPDF}
                  className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 bg-red-600 hover:bg-red-700 disabled:bg-state-disabled text-white rounded-lg transition-colors text-xs sm:text-sm"
                >
                  <ArrowDownTrayIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">{isGeneratingPDF ? (language === 'id' ? 'Membuat...' : 'Generating...') : 'PDF'}</span>
                  <span className="sm:hidden">PDF</span>
                </button>
                <button
                  onClick={generateDOC}
                  disabled={isGeneratingDOC}
                  className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-state-disabled text-white rounded-lg transition-colors text-xs sm:text-sm"
                >
                  <ArrowDownTrayIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">{isGeneratingDOC ? (language === 'id' ? 'Membuat...' : 'Generating...') : 'DOC'}</span>
                  <span className="sm:hidden">DOC</span>
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Title and Info Section */}
        {isCompleted && effectiveResearchPaper && (
          <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-6 border-b border-border-primary">
            <div className="mb-3 sm:mb-4">
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-text-primary mb-2 break-words">
                {effectiveResearchPaper?.title || (language === 'id' ? 'Makalah Penelitian' : 'Research Paper')}
              </h1>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-text-tertiary">
              <div className="flex items-center gap-1 sm:gap-2">
                <span>📊</span>
                <span>{getResearchPaperForCharts().results?.dataVisualization?.length || 0} {language === 'id' ? 'Grafik' : 'Charts'}</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <span>📚</span>
                <span>{academicSources?.length || 0} {language === 'id' ? 'Sumber' : 'Sources'}</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <span>📄</span>
                <span>~{effectiveResearchPaper.metadata?.wordCount || 5000} {language === 'id' ? 'Kata' : 'Words'}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Progress Section */}
       {isGenerating && !isCompleted && (
         <div className="max-w-6xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
          <div className="bg-bg-secondary rounded-xl p-6 border border-border-primary">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-primary">{language === 'id' ? 'Membuat Makalah Penelitian' : 'Generating Research Paper'}</h3>
              <div className="text-right">
                <div className="text-sm font-medium text-text-primary">
                  {language === 'id' ? 'Memproses...' : 'Processing...'}
                </div>
                <div className="text-xs text-text-tertiary">
                  {language === 'id' ? 'Mohon tunggu' : 'Please wait'}
                </div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-bg-tertiary rounded-full h-3 mb-2">
              <div 
                className="bg-emerald-500 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${generationProgress}%` }}
              />
            </div>
            <div className="text-right text-xs text-text-tertiary mb-2">
              {generationProgress}% {language === 'id' ? 'Selesai' : 'Complete'}
            </div>
            
            {/* Warning Text */}
            <p className="text-xs text-text-tertiary mb-4">⚠️ {language === 'id' ? 'Selama proses sedang berjalan jangan tutup tab browser Anda.' : 'Do not close your browser tab while the process is running.'}</p>
            
            {/* Current Section */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                <div>
                  <p className="text-base font-medium text-blue-400">
                    {language === 'id' ? 'Sedang membuat research paper yang komprehensif...' : 'Creating comprehensive research paper...'}
                  </p>
                  <p className="text-sm text-blue-300">
                    {language === 'id' ? 'Menganalisis data dan menyusun konten akademik' : 'Analyzing data and composing academic content'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Download Progress Section */}
      {(isGeneratingPDF || isGeneratingDOC) && (
        <div className="max-w-6xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
          <div className="bg-bg-secondary rounded-xl p-6 border border-border-primary">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-primary">
                {isGeneratingPDF ? (language === 'id' ? 'Membuat PDF...' : 'Generating PDF...') : (language === 'id' ? 'Membuat DOC...' : 'Generating DOC...')}
              </h3>
              <div className="text-right">
                <div className="text-sm font-medium text-text-primary">
                  {language === 'id' ? 'Memproses...' : 'Processing...'}
                </div>
                <div className="text-xs text-text-tertiary">
                  {language === 'id' ? 'Mohon tunggu' : 'Please wait'}
                </div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-bg-tertiary rounded-full h-3 mb-2">
              <div 
                className="bg-blue-500 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${generationProgress}%` }}
              />
            </div>
            <div className="text-right text-xs text-text-tertiary mb-2">
              {generationProgress}% {language === 'id' ? 'Selesai' : 'Complete'}
            </div>
            
            {/* Current Section */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                <div>
                  <p className="text-base font-medium text-blue-400">
                    {isGeneratingPDF ? (language === 'id' ? 'Menangkap grafik dan membuat PDF...' : 'Capturing charts and generating PDF...') : (language === 'id' ? 'Menangkap grafik dan membuat dokumen Word...' : 'Capturing charts and generating Word document...')}
                  </p>
                  <p className="text-sm text-blue-300">
                    {language === 'id' ? 'Menyertakan elemen visual dan pemformatan' : 'Including visual elements and formatting'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Completion Message */}
      {isCompleted && effectiveResearchPaper && !isGeneratingPDF && !isGeneratingDOC && (
        <div className="max-w-6xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-6">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-emerald-400">{language === 'id' ? 'Research Paper Berhasil Dibuat!' : 'Research Paper Successfully Created!'}</h3>
                <p className="text-base text-emerald-300">
                  {language === 'id' ? 'Research paper komprehensif telah selesai diproses. Anda dapat melihat, menyalin, atau mengunduh hasilnya.' : 'Comprehensive research paper has been successfully processed. You can view, copy, or download the results.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Only show when completed and data is available */}
      {isCompleted && effectiveResearchPaper && (
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-8">
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
            {/* Mobile Section Selector */}
            <div className="lg:hidden">
              <div className="bg-bg-secondary rounded-lg p-3 mb-4">
                <h3 className="text-base font-semibold mb-3 text-text-primary">Research Sections</h3>
                <div className="grid grid-cols-2 gap-2">
                  {sections.map((section) => {
                    const Icon = section.icon
                    return (
                      <button
                        key={section.id}
                        onClick={() => setActiveSection(section.id)}
                        className={`flex items-center gap-2 px-2 py-2 rounded-lg text-left transition-colors text-xs ${
                          activeSection === section.id
                            ? 'bg-brand-primary text-text-primary'
                            : 'text-text-secondary hover:bg-bg-quaternary'
                        }`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{section.name}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Desktop Sidebar Navigation */}
            <div className="hidden lg:block w-64 flex-shrink-0">
              <div className="bg-bg-secondary rounded-lg p-4 sticky top-6">
                <h3 className="text-lg font-semibold mb-4 text-text-primary">Research Sections</h3>
                <nav className="space-y-2">
                  {sections.map((section) => {
                    const Icon = section.icon
                    return (
                      <button
                        key={section.id}
                        onClick={() => setActiveSection(section.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                          activeSection === section.id
                            ? 'bg-brand-primary text-text-primary'
                            : 'text-text-secondary hover:bg-bg-quaternary'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        {section.name}
                      </button>
                    )
                  })}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              <motion.div
                key={activeSection}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-bg-secondary rounded-lg p-3 sm:p-4 lg:p-6"
              >
              {/* Abstract Section */}
              {activeSection === 'abstract' && (
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                     <AcademicCapIcon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400" />
                     Abstract
                   </h2>
                  <div className="space-y-3 sm:space-y-4">
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-400 mb-2 text-sm sm:text-base">Background</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.abstract?.background || (language === 'id' ? 'Informasi latar belakang tidak tersedia' : 'No background information available')}</p>
                    </div>
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-green-400 mb-2 text-sm sm:text-base">Objective</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.abstract?.objective || (language === 'id' ? 'Informasi tujuan tidak tersedia' : 'No objective information available')}</p>
                    </div>
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-yellow-400 mb-2 text-sm sm:text-base">Methods</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.abstract?.methods || (language === 'id' ? 'Informasi metode tidak tersedia' : 'No methods information available')}</p>
                    </div>
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-purple-400 mb-2 text-sm sm:text-base">Results</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.abstract?.results || (language === 'id' ? 'Informasi hasil tidak tersedia' : 'No results information available')}</p>
                    </div>
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-red-400 mb-2 text-sm sm:text-base">Conclusion</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.abstract?.conclusion || (language === 'id' ? 'Informasi kesimpulan tidak tersedia' : 'No conclusion information available')}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Introduction Section */}
              {activeSection === 'introduction' && (
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                    <AcademicCapIcon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400" />
                    Introduction
                  </h2>
                  <div className="space-y-3 sm:space-y-6">
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-400 mb-2 text-sm sm:text-base">Background</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.introduction?.background || (language === 'id' ? 'Informasi latar belakang tidak tersedia' : 'No background information available')}</p>
                    </div>
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-green-400 mb-2 text-sm sm:text-base">Problem Statement</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.introduction?.problemStatement || (language === 'id' ? 'Pernyataan masalah tidak tersedia' : 'No problem statement available')}</p>
                    </div>
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-yellow-400 mb-2 text-sm sm:text-base">Research Objectives</h4>
                      <ul className="text-text-secondary space-y-1 text-sm sm:text-base">
                        {effectiveResearchPaper?.introduction?.objectives?.map((objective, index) => (
                          <li key={index} className="flex items-start gap-2 leading-relaxed">
                            <span className="text-yellow-400 mt-1 text-xs sm:text-sm">•</span>
                            <span className="break-words">{objective}</span>
                          </li>
                        )) || <li className="text-text-tertiary">{language === 'id' ? 'Tujuan tidak tersedia' : 'No objectives available'}</li>}
                      </ul>
                    </div>
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-purple-400 mb-2 text-sm sm:text-base">Significance</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.introduction?.significance || (language === 'id' ? 'Informasi signifikansi tidak tersedia' : 'No significance information available')}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Literature Review Section */}
              {activeSection === 'literature' && (
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                    <DocumentTextIcon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400" />
                    Literature Review
                  </h2>
                  <div className="space-y-3 sm:space-y-6">
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-400 mb-2 text-sm sm:text-base">Theoretical Framework</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.literatureReview?.theoreticalFramework || (language === 'id' ? 'Kerangka teoretis tidak tersedia' : 'No theoretical framework available')}</p>
                    </div>
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-green-400 mb-2 text-sm sm:text-base">Previous Studies</h4>
                      {effectiveResearchPaper?.literatureReview?.previousStudies?.length > 0 ? (
                        <div className="space-y-2 sm:space-y-3">
                          {effectiveResearchPaper.literatureReview.previousStudies.map((study, index) => (
                            <div key={index} className="bg-bg-tertiary p-2 sm:p-3 rounded">
                              <p className="font-medium text-text-primary text-sm sm:text-base break-words">{study?.author || (language === 'id' ? 'Tidak Diketahui' : 'Unknown')} ({study?.year || (language === 'id' ? 'T/A' : 'N/A')})</p>
                              <p className="text-xs sm:text-sm text-text-secondary italic break-words">{study?.title || (language === 'id' ? 'Judul tidak tersedia' : 'No title available')}</p>
                                <p className="text-xs sm:text-sm text-text-tertiary mt-1 leading-relaxed">{study?.findings || (language === 'id' ? 'Temuan tidak tersedia' : 'No findings available')}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                          <p className="text-text-tertiary text-sm sm:text-base">{language === 'id' ? 'Studi sebelumnya tidak tersedia' : 'No previous studies available'}</p>
                        )}
                    </div>
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-yellow-400 mb-2 text-sm sm:text-base">Research Gap</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.literatureReview?.researchGap || (language === 'id' ? 'Informasi kesenjangan penelitian tidak tersedia' : 'No research gap information available')}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Methodology Section */}
              {activeSection === 'methodology' && (
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                    <DocumentTextIcon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400" />
                    Methodology
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-400 mb-2 text-sm sm:text-base">Research Design</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.methodology?.researchDesign || (language === 'id' ? 'Informasi desain penelitian tidak tersedia' : 'No research design information available')}</p>
                    </div>
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-green-400 mb-2 text-sm sm:text-base">Participants</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.methodology?.participants || (language === 'id' ? 'Informasi partisipan tidak tersedia' : 'No participants information available')}</p>
                    </div>
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-yellow-400 mb-2 text-sm sm:text-base">Data Collection</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.methodology?.dataCollection || (language === 'id' ? 'Informasi pengumpulan data tidak tersedia' : 'No data collection information available')}</p>
                    </div>
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-purple-400 mb-2 text-sm sm:text-base">Data Analysis</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.methodology?.dataAnalysis || (language === 'id' ? 'Informasi analisis data tidak tersedia' : 'No data analysis information available')}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Results Section */}
              {activeSection === 'results' && (
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                    <ChartBarIcon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400" />
                    Results
                  </h2>
                  
                  {/* Main Findings */}
                  <div className="mb-6 sm:mb-8">
                    <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Main Findings</h3>
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.results?.descriptiveStatistics || (language === 'id' ? 'Statistik deskriptif tidak tersedia' : 'No descriptive statistics available')}</p>
                    </div>
                  </div>

                  {/* Data Visualizations */}
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">Data Visualizations</h3>
                    <div className="space-y-4 sm:space-y-6 md:space-y-8">
                      {(() => {
                        const chartData = getResearchPaperForCharts()
                        
                        if (chartData?.results?.dataVisualization && chartData.results.dataVisualization.length > 0) {
                          return chartData.results.dataVisualization.map((chart, index) => (
                            <div key={`chart-container-${index}`} className="w-full max-w-full overflow-hidden">
                              {renderChart(chart, index)}
                            </div>
                          ))
                        } else {
                          return <p className="text-text-tertiary text-sm sm:text-base">{language === 'id' ? 'Visualisasi data tidak tersedia' : 'No data visualizations available'}</p>
                        }
                      })()} 
                    </div>
                  </div>
                </div>
              )}

              {/* Discussion Section */}
              {activeSection === 'discussion' && (
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                    <DocumentTextIcon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400" />
                    Discussion
                  </h2>
                  <div className="space-y-3 sm:space-y-6">
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-400 mb-2 text-sm sm:text-base">Interpretation</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.discussion?.interpretation || (language === 'id' ? 'Interpretasi tidak tersedia' : 'No interpretation available')}</p>
                    </div>
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-green-400 mb-2 text-sm sm:text-base">Theoretical Implications</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.discussion?.theoreticalImplications || (language === 'id' ? 'Implikasi teoretis tidak tersedia' : 'No theoretical implications available')}</p>
                    </div>
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-yellow-400 mb-2 text-sm sm:text-base">Practical Implications</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.discussion?.practicalImplications || (language === 'id' ? 'Implikasi praktis tidak tersedia' : 'No practical implications available')}</p>
                    </div>
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-red-400 mb-2 text-sm sm:text-base">Limitations</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.discussion?.limitations || (language === 'id' ? 'Keterbatasan tidak tersedia' : 'No limitations available')}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Conclusion Section */}
              {activeSection === 'conclusion' && (
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                    <DocumentTextIcon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400" />
                    Conclusion
                  </h2>
                  <div className="space-y-3 sm:space-y-6">
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-400 mb-2 text-sm sm:text-base">Summary</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.conclusion?.summary || (language === 'id' ? 'Ringkasan tidak tersedia' : 'No summary available')}</p>
                    </div>
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-green-400 mb-2 text-sm sm:text-base">Contributions</h4>
                      <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{effectiveResearchPaper?.conclusion?.contributions || (language === 'id' ? 'Kontribusi tidak tersedia' : 'No contributions available')}</p>
                    </div>
                    <div className="bg-bg-quaternary p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-yellow-400 mb-2 text-sm sm:text-base">Recommendations</h4>
                      <ul className="text-text-secondary space-y-1 text-sm sm:text-base">
                        {effectiveResearchPaper?.conclusion?.recommendations?.length > 0 ? (
                          effectiveResearchPaper.conclusion.recommendations.map((rec, index) => (
                            <li key={index} className="flex items-start gap-2 leading-relaxed">
                              <span className="text-yellow-400 mt-1 text-xs sm:text-sm">•</span>
                              <span className="break-words">{rec}</span>
                            </li>
                          ))
                        ) : (
                          <li className="text-text-tertiary">{language === 'id' ? 'Rekomendasi tidak tersedia' : 'No recommendations available'}</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* References Section */}
              {activeSection === 'references' && (
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                    <AcademicCapIcon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400" />
                    Abstract
                  </h2>
                  <div className="space-y-4">
                    {academicSources?.length > 0 ? (
                      academicSources.map((source, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="bg-bg-quaternary p-4 rounded-lg border-l-4 border-blue-500"
                        >
                          <h4 className="font-semibold text-text-primary mb-2">{source.title}</h4>
                          <p className="text-text-secondary text-sm mb-2">{source.snippet}</p>
                          <a 
                            href={source.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300 text-sm"
                          >
                            {source.url}
                          </a>
                        </motion.div>
                      ))
                    ) : (
                      <p className="text-text-tertiary">{language === 'id' ? 'Tidak ada sumber akademik tersedia' : 'No academic sources available'}</p>
                    )}
                  </div>
                </div>
              )}
              </motion.div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ResearchResultPage